/**
 * Welcome (marketing) page — React port of the previous public/welcome.html.
 *
 * Same design language (gradient hero, terminal mock, problem→solution panels,
 * 6 features, architecture diagram, industry pack card, trust strip, code
 * block, final CTA) but as a real React route so:
 *  - it shares the app theme variables (--bg/--accent/etc.),
 *  - it can use TanStack <Link> for in-app navigation,
 *  - it lives under the public branch of Layout, which already mounts
 *    <BrandFooter /> below the page.
 *
 * Styling is a single scoped <style> block under .welcome-fx so the dense
 * marketing-only CSS does not leak into the Studio surfaces.
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  FlaskConical,
  GitBranch,
  HeartPulse,
  Landmark,
  Network,
  Scale,
  Shield,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import { useApp } from "@/lib/store";
import { ContactModal } from "@/components/ContactModal";
import ComposerROI from "@/components/ComposerROI";
import DecisionTable from "@/components/DecisionTable";
import DevExperience from "@/components/DevExperience";
import ExecutiveValue from "@/components/ExecutiveValue";
import PrototypePortfolio from "@/components/PrototypePortfolio";
import SemanticFabric from "@/components/SemanticFabric";
import SolutionShowcase from "@/components/SolutionShowcase";
import ArchitectureHero from "@/components/ArchitectureHero";
import HeroVideo from "@/components/HeroVideo";
import PromoVideoDialog from "@/components/PromoVideoDialog";
import { prototypeUrl } from "@/lib/showcase";

const HERO_DEMO_URL = prototypeUrl("underwriting-amplifier");

function emitMarketingEvent(event: string, data: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  const payload = { event, section: "welcome", ts: Date.now(), ...data };
  window.dispatchEvent(new CustomEvent("ontoligent:analytics", { detail: payload }));
  const w = window as Window & { dataLayer?: Array<Record<string, unknown>>; plausible?: (name: string, opts?: Record<string, unknown>) => void };
  if (Array.isArray(w.dataLayer)) {
    w.dataLayer.push(payload);
  }
  if (typeof w.plausible === "function") {
    w.plausible(event, { props: data });
  }
}

// Installable vertical packs. Switchable on the marketing page so visitors can
// see the breadth of domains, not just the financial-services flagship.
//
// Track A.4 (Ontoligent GA readiness) — every pack now carries an explicit
// ``availability`` state so the marketing surface stops implying every
// vertical is generally available today. The TypeScript union forces new
// packs to pick a state consciously; there is no "unset" default.
//
// * ``available``    — pack is composable NOW (industry + universal core
//                       tiers authored, regression suite green).
// * ``in_progress``  — pack skeleton exists but content authoring is
//                       paused pending SME (actuarial / clinical / legal)
//                       review. Marketing may reference it as a roadmap
//                       item, never as a shippable asset.
// * ``not_available`` — pack has no authored skeleton yet; showing it on
//                       the marketing page is directional / illustrative
//                       and MUST NOT be sold as production-ready.
type PackAvailability = "available" | "in_progress" | "not_available";

interface DomainPack {
  id: string;
  tab: string;
  title: string;
  desc: string;
  availability: PackAvailability;
  availability_note?: string;
  metrics: Array<[string, string]>;
  includes: string[];
  readiness: Array<[string, string]>;
  badges: string[];
  uses: Array<[string, string, string]>;
}
const DOMAIN_PACKS: DomainPack[] = [
  {
    id: "fibo-acord",
    tab: "Insurance",
    title: "Ontoligent Insurance — Party, Policy, Claims, Financial, Regulatory",
    desc: "Enterprise underwriting and claims intelligence with policy semantics, regulatory controls (IDD, Solvency II, IFRS 17, DORA, SFCR, ORSA, IPID) and governed writeback ready for production.",
    availability: "available",
    availability_note: "Insurance is the flagship vertical — 8 Tier 2 packs + Tier 0.5 universal substrate + SHACL/OWL-RL validation, all covered by the golden-query regression suite. FIBO / ACORD themselves are NOT bundled (see Composer → Standards for their availability).",
    metrics: [["6", "retrieval profiles"], ["4", "ready agents"], ["200+", "regression evals"]],
    includes: ["Enterprise ontology", "Knowledge graph model", "SHACL validation", "Agent templates", "Retrieval profiles", "Evaluation suite", "Documentation", "Signed ORB"],
    readiness: [["Composition gates", "Passed"], ["Typical scaffold", "< 1 hour"], ["Knowledge maturity", "Production candidate"], ["ORB package", "Signed"], ["Instance validation", "Customer data required"], ["Governance", "Included"]],
    badges: ["IDD", "Solvency II", "IFRS 17", "DORA", "Control mapping", "Customer-hosted"],
    uses: [
      ["Claims triage", "Connect claims systems, policy clauses, repair estimates and customer history into one governed decision workflow.", "UC-02"],
      ["KYC and EDD enrichment", "Unify customer, counterparty and sanctions evidence across systems with traceable entity resolution.", "UC-04"],
      ["Filings and research synthesis", "Generate cited executive narratives from mixed regulatory filings and analyst research.", "UC-07"],
      ["Subrogation scout", "Map relationship paths to recovery opportunities with confidence-scored evidence trails.", "UC-09"],
    ],
  },
  {
    id: "banking-kyc",
    tab: "Banking KYC",
    title: "Ontoligent Banking KYC — Customer Due Diligence & AML",
    desc: "Additive Tier 2 overlay for banking KYC — customer risk categorization, PEP screening, sanctions cross-checks and enhanced due diligence workflows on top of the universal party / financial / regulatory substrate.",
    availability: "available",
    availability_note: "Banking is an additive Tier 2 overlay — not a replacement for insurance. Ships with regulatory-banking (AMLD5/6, PSD2, FATCA) and party-banking (beneficial ownership, PEP, sanction lists).",
    metrics: [["3", "banking packs"], ["AMLD5/6", "regulatory scope"], ["additive", "no insurance conflict"]],
    includes: ["Enterprise ontology", "Knowledge graph model", "SHACL validation", "Agent templates", "Retrieval profiles", "Evaluation suite", "Documentation", "Signed ORB"],
    readiness: [["Composition gates", "Passed"], ["Typical scaffold", "< 2 hours"], ["Knowledge maturity", "Production candidate"], ["ORB package", "Signed"], ["Instance validation", "Customer data required"], ["Governance", "Included"]],
    badges: ["AMLD5", "AMLD6", "PSD2", "FATCA", "Control mapping"],
    uses: [
      ["Customer due diligence", "Onboard customers with risk-scored KYC evidence, sanction hits and beneficial-owner traversal in one governed workflow.", "UC-KYC-01"],
      ["Ongoing monitoring", "Continuously re-evaluate KYC posture against fresh sanction/PEP list snapshots with audit-ready lineage.", "UC-KYC-02"],
    ],
  },
  {
    id: "orb-med",
    tab: "Life Sciences",
    title: "ORB-Med — Biomedical Research",
    desc: "Production-grade biomedical intelligence with ontology-aligned research entities, causal links and auditable evidence chains.",
    availability: "in_progress",
    availability_note: "Skeleton overlay + LLM-guided ontology generation shipped, but the healthcare/pharma ontology is NOT authored to production standard. Reference the Composer for the actual live tiers today. Clinical SME review pending.",
    metrics: [["10", "entity types"], ["5", "research agents"], ["GraphRAG", "default profile"]],
    includes: ["Enterprise ontology", "Knowledge graph model", "SHACL validation", "Agent templates", "Retrieval profiles", "Evaluation suite", "Documentation", "Signed ORB"],
    readiness: [["Production readiness", "Prototype"], ["Typical deployment", "Not committed"], ["Knowledge maturity", "SME review pending"], ["ORB package", "Draft"], ["Evaluation", "In progress"], ["Governance", "Included"]],
    badges: ["MONDO", "HPO", "GO", "EuropePMC", "ClinicalTrials.gov"],
    uses: [
      ["Research intelligence", "Connect gene, pathway and trial evidence into explainable recommendations for research teams.", "UC-01"],
      ["Drug repurposing scout", "Discover graph-hop target opportunities with provenance from studies and trial outcomes.", "UC-03"],
      ["Contradiction surfacing", "Detect conflicting evidence edges before expensive experiment cycles begin.", "UC-05"],
      ["Hypothesis generation", "Propose missing-link hypotheses with citations, confidence and reproducible lineage.", "UC-06"],
    ],
  },
  {
    id: "mitre",
    tab: "SecOps",
    title: "MITRE ATT&CK — Threat Operations",
    desc: "Cyber operations intelligence for fast threat triage, repeatable response and policy-governed containment decisions.",
    availability: "in_progress",
    availability_note: "MITRE ATT&CK ontology adapter drafted; no production Tier 2 pack yet. Available as reference architecture, not a shippable pack.",
    metrics: [["6", "entity types"], ["MCP", "SIEM / EDR connectors"], ["ATT&CK", "aligned"]],
    includes: ["Enterprise ontology", "Knowledge graph model", "SHACL validation", "Agent templates", "Retrieval profiles", "Evaluation suite", "Documentation", "Signed ORB"],
    readiness: [["Production readiness", "Reference architecture"], ["Typical deployment", "Not committed"], ["Knowledge maturity", "Pack authoring pending"], ["ORB package", "Draft"], ["Evaluation", "In progress"], ["Governance", "Included"]],
    badges: ["MITRE ATT&CK", "MCP", "SIEM", "EDR"],
    uses: [
      ["Threat hunt and enrichment", "Correlate alerts, identities and assets in one graph for higher-confidence investigations.", "UC-11"],
      ["Containment playbook", "Trigger approval-gated response actions scored by confidence, blast radius and cost.", "UC-12"],
      ["Post-incident review", "Replay every decision with lineage, policy checks and remediation outcomes.", "UC-13"],
    ],
  },
  {
    id: "legal",
    tab: "Legal",
    title: "Legal Clause Taxonomy — Contract Intelligence",
    desc: "Contract intelligence that operationalizes playbooks, obligations and redline governance with audit-ready traceability.",
    availability: "in_progress",
    availability_note: "Clause taxonomy skeleton shipped, no production-grade legal ontology authored yet. Directional reference only.",
    metrics: [["5", "entity types"], ["1", "contract agent"], ["50", "redline evals"]],
    includes: ["Enterprise ontology", "Knowledge graph model", "SHACL validation", "Agent templates", "Retrieval profiles", "Evaluation suite", "Documentation", "Signed ORB"],
    readiness: [["Production readiness", "Prototype"], ["Typical deployment", "Not committed"], ["Knowledge maturity", "SME review pending"], ["ORB package", "Draft"], ["Evaluation", "In progress"], ["Governance", "Included"]],
    badges: ["Clause taxonomy", "Diff-aware", "Playbook"],
    uses: [
      ["Contract review and playbook", "Identify clause deviations against approved playbooks with severity-ranked rationale.", "UC-21"],
      ["Redline proposal", "Generate rule-cited suggestions aligned to legal policy and risk posture.", "UC-22"],
      ["Obligation extraction", "Create a typed obligation graph for owners, deadlines and compliance status.", "UC-23"],
    ],
  },
  {
    id: "snomed",
    tab: "Healthcare",
    title: "SNOMED Clinical — Medical Affairs",
    desc: "Medical affairs intelligence for evidence synthesis, indication-aware reasoning and controlled communication workflows.",
    availability: "in_progress",
    availability_note: "Healthcare vertical NOT shipped as a production pack. SNOMED CT itself is separately licensed (UMLS) and NOT bundled by Ontoligent. Directional reference only.",
    metrics: [["5", "entity types"], ["PubMed", "connector"], ["ABAC", "indication / region"]],
    includes: ["Enterprise ontology", "Knowledge graph model", "SHACL validation", "Agent templates", "Retrieval profiles", "Evaluation suite", "Documentation", "Signed ORB"],
    readiness: [["Production readiness", "Reference architecture"], ["Typical deployment", "Not committed"], ["Knowledge maturity", "Licensed source + SME review required"], ["ORB package", "Draft"], ["Evaluation", "In progress"], ["Governance", "Included"]],
    badges: ["SNOMED", "PubMed", "PII redaction"],
    uses: [
      ["Clinical evidence synthesis", "Draft cited responses for MSL teams from studies, indications and safety evidence.", "UC-31"],
      ["Off-label risk flagging", "Apply indication and region policy controls before recommendations are delivered.", "UC-32"],
    ],
  },
];

const PACK_FLOW_STEPS = [
  "Enterprise Pack",
  "ORB Package",
  "Verify Signature",
  "Install",
  "Knowledge Layer",
  "Agents",
  "Enterprise Systems",
];

const PACK_DEPLOY_TARGETS = [
  "Development",
  "Testing",
  "Production",
  "Another business unit",
  "Another country",
  "Another customer",
  "Private cloud",
  "On-premises",
];

const PACK_TAB_ICONS: Record<string, LucideIcon> = {
  "fibo-acord": Building2,
  "banking-kyc": Landmark,
  "orb-med": FlaskConical,
  mitre: Shield,
  legal: Scale,
  snomed: HeartPulse,
};

// Track A.4 — availability badge rendering helper. Explicit ``switch``
// exhaustiveness makes new ``PackAvailability`` enum values cause a
// compile-time failure until the badge copy is authored.
function availabilityBadge(availability: PackAvailability): {
  label: string;
  className: string;
} {
  switch (availability) {
    case "available":
      return {
        label: "Available",
        className: "border-good/40 bg-good/10 text-good",
      };
    case "in_progress":
      return {
        label: "In progress — SME review",
        className: "border-warn/40 bg-warn/10 text-warn",
      };
    case "not_available":
      return {
        label: "Not available",
        className: "border-border bg-bg/50 text-muted",
      };
  }
}

export function Welcome() {
  const user = useApp((s) => s.user);
  const [activePack, setActivePack] = useState(0);
  const [packDetailView, setPackDetailView] = useState<"use-cases" | "includes" | "readiness">("use-cases");
  const [demoOpen, setDemoOpen] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const pack = DOMAIN_PACKS[activePack];

  const trackArchitectureCta = (cta: "explore_architecture" | "see_data_flow") => {
    emitMarketingEvent("marketing_hero_cta_click", {
      hero: "architecture",
      cta,
    });
  };

  return (
    <div className="welcome-fx">
      {/* HERO */}
      <ArchitectureHero
        onExploreArchitecture={() => trackArchitectureCta("explore_architecture")}
        onSeeDataFlow={() => trackArchitectureCta("see_data_flow")}
      />

      <section className="positioning-band" aria-labelledby="positioning-title">
        <div className="container positioning-inner">
          <div className="positioning-intro">
            <p className="section-eyebrow portfolio-eyebrow">The missing intelligence layer</p>
            <h2 id="positioning-title">Scale your data value.<br />Build real AI solutions.</h2>
            <p>
              Ontoligent turns the knowledge already inside your enterprise into
              governed AI products and automation. No new system of record. No
              need to hand us the asset that makes your business unique.
            </p>
            <div className="positioning-flow" aria-label="Protected knowledge becomes governed intelligence and working AI products">
              <div><Shield aria-hidden /><span>Protected</span></div>
              <ArrowRight aria-hidden />
              <div><Network aria-hidden /><span>Intelligent</span></div>
              <ArrowRight aria-hidden />
              <div><Workflow aria-hidden /><span>Working AI</span></div>
            </div>
          </div>
          <div className="positioning-principles">
            <article>
              <Shield className="positioning-ghost" aria-hidden />
              <div className="positioning-marker"><Shield aria-hidden /><span>01</span></div>
              <span className="positioning-kicker">Keep your advantage</span>
              <h3>Your knowledge stays yours</h3>
              <p>In a customer-controlled deployment, data stays in your environment, under your security, governance and keys.</p>
            </article>
            <article>
              <Network className="positioning-ghost" aria-hidden />
              <div className="positioning-marker"><GitBranch aria-hidden /><span>02</span></div>
              <span className="positioning-kicker">Add what is missing</span>
              <h3>Make it usable by AI</h3>
              <p>Ontoligent adds shared meaning, context and controls across the systems you already run, without replacing them.</p>
            </article>
            <article>
              <Workflow className="positioning-ghost" aria-hidden />
              <div className="positioning-marker"><BadgeCheck aria-hidden /><span>03</span></div>
              <span className="positioning-kicker">Deliver the outcome</span>
              <h3>Build valuable products</h3>
              <p>Launch reliable agents, better decisions and controlled process automation grounded in how your business actually operates.</p>
            </article>
          </div>
        </div>
      </section>

      <PrototypePortfolio onContact={() => setDemoOpen(true)} />

      {/* COMPOSER ROI — replaces the previous "Inside Ontoligent" product
         showcase. Ports the Studio Composer's business case (6-8 wk hand
         author vs ~20 min composed) from
         D:/assets/filesimproved/ontoligent-roi-section.html.
         See src/components/ComposerROI.tsx. */}
      <ComposerROI />
        {/* Trust proof follows the business case so risk and compliance buyers
          reach the verified-governance argument before technical detail. */}
        <ExecutiveValue />
      {/* Why Ontoligent — decision-table Turn 2 §1 replacement.
         Old inline `<section id="platform">` ledger + why-strip
         (Retrieval/Citations/Ontology/Failures/Spend & risk plus 4 pills)
         moved into a dedicated component so it can be maintained
         alongside the source mockup at
         `d:/assets/filesimproved/ontoligent-decision-table-section.html`.
         See `src/components/DecisionTable.tsx`. */}
      <DecisionTable />

      {/* Chatbot Amplifier before/after + prototype solution cards, each
        linking directly to its matching public prototype.
         See `src/components/SolutionShowcase.tsx` (Phase 14.2). */}
      <SolutionShowcase />

      {/* A second, media-led story without replacing the architecture hero. */}
      <section id="action" aria-labelledby="action-title">
        <div className="container">
          <div className="action-showcase">
            <div className="action-story">
              <div className="action-kicker"><span>Governed intelligence in motion</span></div>
              <h2 id="action-title">From answer<br />to <em>action.</em></h2>
              <p className="action-lede">See how scattered enterprise knowledge becomes a decision you can trust — and an action you can control.</p>
              <p className="action-support">Ontoligent connects source evidence, business context and agent reasoning, then carries approval and lineage through to execution.</p>
              <div className="action-cta">
                <button type="button" className="btn btn-primary" onClick={() => {
                  emitMarketingEvent("marketing_video_open", { placement: "action" });
                  setPromoOpen(true);
                }}>Watch the platform in action <ArrowRight size={16} aria-hidden /></button>
                <a className="btn" href={HERO_DEMO_URL} target="_blank" rel="noopener noreferrer">Explore a live workflow <ArrowRight size={15} aria-hidden /></a>
              </div>
              <div className="action-story-foot">Source evidence <span>→</span> Governed decision <span>→</span> Controlled action</div>
            </div>
            <div className="action-video"><HeroVideo /></div>
          </div>
          <div className="action-sequence-head"><span>Built for the full decision cycle</span><span>01 — 04</span></div>
          <div className="act-grid" role="list" aria-label="Governed action lifecycle">
            {[
              { h: "Action Types", p: "Declarative writeback contracts: inputs, validators, side-effects. Versioned with the ontology." },
              { h: "Writeback connectors", p: "Push to Salesforce, ServiceNow, Jira, core banking, claims systems. Same ABAC as the read path." },
              { h: "Approval workflows", p: "Route high-risk actions to a human; capture the decision in the audit log." },
              { h: "Decision lineage", p: "Every action carries the lineage_id of the answer that triggered it. Replay end-to-end." },
            ].map((a, index) => (
              <article key={a.h} className="act-step" role="listitem">
                <span className="act-step-no">0{index + 1}</span>
                <h3>{a.h}</h3>
                <p>{a.p}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <aside className="mid-cta" aria-label="Interactive prototype walkthrough">
        <div>
          <span>Ready to inspect the workflow?</span>
          <strong>Follow a submission from intake to governed decision.</strong>
        </div>
        <a className="btn-primary btn" href={HERO_DEMO_URL} target="_blank" rel="noopener noreferrer">
          See the full walkthrough <ArrowRight size={15} aria-hidden />
        </a>
      </aside>
      <br />

      {/* Consolidates the former Objects, Features, Runtime and composition
          explanations into one architecture module. Technical execution proof
          follows it for evaluators who need API and trace detail. */}
      <SemanticFabric />
      <DevExperience />

      {/* ENTERPRISE KNOWLEDGE PACKS */}
      <section id="packs">
        <div className="container">
          <div className="section-head pack-head">
            <div>
              <div className="section-eyebrow">Enterprise Knowledge Packs</div>
              <h2>Industry intelligence, ready to specialize.</h2>
            </div>
            <p className="section-sub">
              Start with a governed, validated foundation, then complete customer-data and acceptance gates for production.
            </p>
          </div>

          <div className="pack-ladder" aria-label="Enterprise knowledge pack lifecycle">
            {["Knowledge packs", "Industry semantics", "Agentic execution"].map((step, i) => (
              <div key={step} className="pack-ladder-step">
                <span>{step}</span>
                {i < 2 ? <i aria-hidden>{"->"}</i> : null}
              </div>
            ))}
          </div>

          <div
            className="pack-tabs"
            role="tablist"
            aria-label="Enterprise knowledge packs"
            style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginBottom: 18, marginTop: 14 }}
          >
            {DOMAIN_PACKS.map((p, i) => {
              const on = i === activePack;
              const TabIcon = PACK_TAB_ICONS[p.id];
              const badge = availabilityBadge(p.availability);
              return (
                <button
                  key={p.id}
                  role="tab"
                  aria-selected={on}
                  aria-label={`${p.tab} — ${badge.label}`}
                  onClick={() => {
                    setActivePack(i);
                    setPackDetailView("use-cases");
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "7px 14px",
                    borderRadius: 999,
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: on ? 600 : 500,
                    border: `1px solid ${on ? "hsl(var(--accent))" : "hsl(var(--border))"}`,
                    background: on ? "hsl(var(--accent) / 1)" : "transparent",
                    color: on ? "hsl(var(--accent-foreground))" : "hsl(var(--text))",
                    transition: "all .15s ease",
                  }}
                >
                  {TabIcon ? <TabIcon size={14} aria-hidden /> : null}
                  {p.tab}
                </button>
              );
            })}
          </div>
          <div className="pack-card" key={pack.id}>
            <div className="pack-main">
              <div className="pack-kicker">Featured enterprise pack</div>
              <h3 className="pack-title">{pack.title}</h3>
              <div
                role="status"
                aria-live="polite"
                className={`mt-2 inline-flex items-center gap-2 rounded-[7px] border px-2.5 py-1 font-mono text-[11px] uppercase tracking-[0.05em] ${availabilityBadge(pack.availability).className}`}
              >
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
                {availabilityBadge(pack.availability).label}
              </div>
              {pack.availability_note && (
                <p className="mt-2 text-[12px] leading-relaxed text-muted">
                  {pack.availability_note}
                </p>
              )}
              <p className="pack-desc">{pack.desc}</p>
              <div className="pack-metrics">
                {pack.metrics.map(([n, l]) => (
                  <div key={l}><strong>{n}</strong><span>{l}</span></div>
                ))}
              </div>
              <div className="pack-badges">
                {pack.badges.map((b) => (
                  <span key={b} className="pill">{b}</span>
                ))}
              </div>
            </div>
            <div className="pack-uses">
              <div className="pack-detail-tabs" role="tablist" aria-label="Pack details">
                {[
                  ["use-cases", "Use Cases"],
                  ["includes", "Includes"],
                  ["readiness", "Readiness"],
                ].map(([key, label]) => {
                  const on = packDetailView === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={on}
                      className={`pack-detail-tab${on ? " on" : ""}`}
                      onClick={() => setPackDetailView(key as "use-cases" | "includes" | "readiness")}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <div className="pack-side-card">
                <h4>
                  {packDetailView === "use-cases" && "Reference use cases"}
                  {packDetailView === "includes" && "Included capabilities"}
                  {packDetailView === "readiness" && "Production profile"}
                </h4>
                {packDetailView === "use-cases" && (
                  <>
                    {pack.uses.map(([t, s, c]) => (
                      <div className="uc" key={c}>
                        <span><strong>{t}</strong><em>{s}</em></span><code>{c}</code>
                      </div>
                    ))}
                  </>
                )}
                {packDetailView === "includes" && (
                  <div className="pack-include-grid compact">
                    {pack.includes.map((item) => (
                      <span key={item}>{item}</span>
                    ))}
                  </div>
                )}
                {packDetailView === "readiness" && (
                  <div className="pack-readiness">
                    {pack.readiness.map(([label, value]) => (
                      <div key={label} className="pack-readiness-row">
                        <span>{label}</span>
                        <strong>{value}</strong>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 18, textAlign: "center" }}>
            <Link className="btn" to="/use-cases">
              Explore use cases & implementation →
            </Link>
          </div>
        </div>
      </section>

      {/* ORB DEPLOYMENT */}
      <section id="orb">
        <div className="container">
          <div className="section-head orb-head">
            <div>
              <div className="section-eyebrow">Powered by ORB(TM)</div>
              <h2>Signed, verifiable enterprise knowledge deployment.</h2>
            </div>
            <p className="section-sub">
              ORB packages turn Knowledge Packs into trusted deployment artifacts:
              versioned, signed, auditable and repeatable across environments.
            </p>
          </div>
          <div className="orb-grid">
            <article className="orb-panel">
              <h3>Deploy once. Reuse everywhere.</h3>
              <p>
                Treat enterprise knowledge like software release units.
                Verify signature, install in minutes, and keep semantics aligned
                across development, testing, production and regulated operations.
              </p>
              <div className="orb-checks">
                {["Versioned", "Signed", "Verified", "Repeatable", "Auditable"].map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </article>
            <article className="orb-panel">
              <h4>Deployment flow</h4>
              <div className="pack-flow" role="img" aria-label="Enterprise pack deployment flow">
                {PACK_FLOW_STEPS.map((step, i) => (
                  <div key={step} className="pack-flow-step">
                    <span>{step}</span>
                    {i < PACK_FLOW_STEPS.length - 1 ? <i aria-hidden>{"->"}</i> : null}
                  </div>
                ))}
              </div>
              <h4 style={{ marginTop: 16 }}>Supported targets</h4>
              <div className="pack-targets">
                {PACK_DEPLOY_TARGETS.map((target) => (
                  <span key={target}>{target}</span>
                ))}
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* GOVERNANCE / EXECUTIVE VALUE — replaces the old technical "Trust"
         panel with an outcomes-first section for CDOs, data-governance and
         data-quality owners, and risk/compliance execs. Business language,
         one concrete motor-claims example, and an ROI strip. Each claim maps
         to a real mechanism (SHACL DQ gate, signed packs, cited/versioned
         ORB). See `src/components/ExecutiveValue.tsx`. */}
      {/* Developer experience previously lived here as a separate <section
         id="docs">. It is now folded into the unified <DevExperience /> panel
         mounted above (id="proof"), which combines the trace stream and the
         REST/MCP code blocks into one tabbed terminal. */}

      {/* THE LOOP — adapted from the LangChain hero (4 word-tags + animated arcs
         converging on a central node). Pure SVG/CSS, no Lottie dependency.
         The four stages are Ontoligent's pillars: ingest → reason → govern → act. */}
      <section id="loop" className="loop-section">
        <div className="container">
          <div className="section-head">
            <div>
              <div className="section-eyebrow">The loop</div>
              <h2>Ingest. Reason. Govern. Act.</h2>
            </div>
            <p className="section-sub">
              One closed loop across your knowledge, your agents, and your
              systems-of-record — every step cited, permissioned, and replayable,
              ending in a deliverable you can defend.
            </p>
          </div>

          <div className="loop-stage">
            <svg className="loop-art" viewBox="0 0 1200 520" preserveAspectRatio="xMidYMid meet" role="img"
                 aria-label="Four stages — Ingest, Reason, Govern, Act — converge into a governed, audited deliverable.">
              <defs>
                <marker id="loop-arrow" markerWidth="10" markerHeight="10" refX="6" refY="3"
                        orient="auto" markerUnits="strokeWidth">
                  <path d="M0 0 L6 3 L0 6 z" fill="hsl(var(--accent))" />
                </marker>
              </defs>
              {/* Stage nodes (aligned: arcs start at each node's bottom-center) */}
              {[
                { cx: 150, label: "01 Ingest", meta: "source / normalize" },
                { cx: 450, label: "02 Reason", meta: "context / resolve" },
                { cx: 750, label: "03 Govern", meta: "policy / approve" },
                { cx: 1050, label: "04 Act", meta: "writeback / receipt" },
              ].map((n) => (
                
                <g key={n.label} className="loop-stagenode">
                  
                  <g className="loop-stage-stack">
                    <polygon points={`${n.cx - 80},48 ${n.cx - 44},34 ${n.cx - 8},48 ${n.cx - 44},62`} />
                    <polygon points={`${n.cx - 80},38 ${n.cx - 44},24 ${n.cx - 8},38 ${n.cx - 44},52`} />
                    <polygon points={`${n.cx - 80},28 ${n.cx - 44},14 ${n.cx - 8},28 ${n.cx - 44},42`} />
                    <circle cx={n.cx - 44} cy="28" r="2.5" />
                  </g>
                  <text x={n.cx + 46} y="36" textAnchor="middle" style={{  marginLeft: 8, fontFamily:"'IBM Plex Mono', ui-monospace, monospace" }}>
                    {n.label}
                  </text>
                  <text x={n.cx + 46} y="56" textAnchor="middle" className="loop-stage-meta"  style={{  marginLeft: 8 }}>
                    {n.meta}
                  </text>
                </g>
                
              ))}

              {/* Converging arcs — base layer (faint, always visible) */}
              <g className="loop-base">
                <path d="M 106 62  C 106 210, 420 300, 596 332" />
                <path d="M 406 62  C 406 190, 540 280, 596 332" />
                <path d="M 706 62  C 706 190, 660 280, 604 332" />
                <path d="M 1006 62 C 1006 210, 780 300, 604 332" />
              </g>

              {/* Bright trace sweep racing toward the hub */}
              <g className="loop-trace">
                <path className="loop-path loop-path-1" d="M 106 62  C 106 210, 420 300, 596 332" pathLength={100} />
                <path className="loop-path loop-path-2" d="M 406 62  C 406 190, 540 280, 596 332" pathLength={100} />
                <path className="loop-path loop-path-3" d="M 706 62  C 706 190, 660 280, 604 332" pathLength={100} />
                <path className="loop-path loop-path-4" d="M 1006 62 C 1006 210, 780 300, 604 332" pathLength={100} />
              </g>

              {/* Hub */}
              <circle className="loop-hub-ring" cx="600" cy="332" r="20" />
              <circle className="loop-node" cx="600" cy="332" r="7" />

              {/* Flow down into the deliverable */}
              <line className="loop-axis" x1="600" y1="352" x2="600" y2="398" markerEnd="url(#loop-arrow)" />

              {/* Deliverable card — the flow ends in something defensible */}
              <g className="loop-deliverable">
                <rect x="346" y="406" rx="16" ry="16" width="508" height="86" />
                <text x="600" y="440" textAnchor="middle" className="loop-deliv-title">Governed deliverable</text>
                <text x="600" y="468" textAnchor="middle" className="loop-deliv-sub">cited · permissioned · reversible · audited · tracked</text>
              </g>
            </svg>
          </div>

          


        </div>
      </section>

      {/* CTA */}
      <section id="cta" className="cta-final">
        <div className="container">
          <h2>Test one governed decision from your domain.</h2>
          <p>
            Bring a representative workflow and the questions that block deployment.
            We will map the evidence, authority, action path, and measurable outcome.
          </p>
          <p className="cta-identity-line">
            Enterprise Intelligence begins where Artificial Intelligence reaches its limits.
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button className="btn-primary btn" onClick={() => setDemoOpen(true)}>Book a demo</button>
            <Link className="btn" to="/signup">Self-serve signup</Link>
            <Link className="btn" to="/use-cases">Use cases & blueprints</Link>
          </div>
        </div>
      </section>

      <ContactModal
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        kind="demo"
        title="Book a demo"
        subtitle="Map a representative workflow from source evidence through authority, action and measurable outcome with the Ontoligent team."
        cta="Request demo"
        defaultEmail={user?.email || ""}
      />
      <PromoVideoDialog
        open={promoOpen}
        onClose={() => setPromoOpen(false)}
        onPlay={() => emitMarketingEvent("marketing_video_play", { placement: "action" })}
        onEnded={() => emitMarketingEvent("marketing_video_complete", { placement: "action" })}
      />

      <style>{`
        .loop-stage-num   { font: 500 11px var(--font-mono, ui-monospace, monospace); letter-spacing: .12em;
          fill: hsl(var(--accent)); text-anchor: start; }
        .loop-stage-label { font: 600 22px var(--font-sans, system-ui); fill: hsl(var(--foreground)); text-anchor: start; }
        .loop-stage-out   { font: 400 14px var(--font-sans, system-ui); fill: hsl(var(--muted-foreground)); text-anchor: start; }
        .loop-return      { fill: none; stroke: hsl(var(--accent) / .45); stroke-width: 1; stroke-dasharray: 4 5; }
        .loop-return-label{ font: 400 11px var(--font-mono, ui-monospace, monospace); letter-spacing: .06em;
                    fill: hsl(var(--muted-foreground)); text-anchor: start; }
        .welcome-fx { background: hsl(var(--bg)); color: hsl(var(--text)); font-family: 'IBM Plex Sans', system-ui, sans-serif; }
        .welcome-fx h1, .welcome-fx h2, .welcome-fx h3, .welcome-fx h4 { font-family: inherit; }
        .welcome-fx .why-head-title { font-size: 1.5em; }
        .welcome-fx .cta-identity-line { margin-top: 6px; font-style: italic; color: hsl(var(--muted)); }
        .welcome-fx .container { max-width: 1200px; margin: 0 auto; padding: 0 32px; }
        .welcome-fx .relative { position: relative; }
        .welcome-fx a { color: inherit; text-decoration: none; }

        .welcome-fx .top { position: sticky; top: 0; z-index: 50; backdrop-filter: blur(10px); background: hsl(var(--bg) / 0.72); border-bottom: 1px solid hsl(var(--border)); }
        .welcome-fx .top .container { display: flex; align-items: center; justify-content: space-between; gap: 16px; min-height: 62px; flex-wrap: wrap; padding-top: 10px; padding-bottom: 10px; }
        .welcome-fx .brand { display: flex; align-items: center; gap: 10px; font-weight: 700; flex-shrink: 0; }
        .welcome-fx .brand-dot { width: 14px; height: 14px; border-radius: 4px; background: linear-gradient(135deg, hsl(var(--accent)), hsl(var(--accent) / 0.7)); box-shadow: 0 0 24px hsl(var(--accent) / 0.5); }
        .welcome-fx .navlinks { display: flex; gap: 28px; color: hsl(var(--muted)); font-size: 14px; flex-wrap: wrap; min-width: 0; }
        .welcome-fx .navlinks a { white-space: nowrap; }
        .welcome-fx .navlinks a:hover { color: hsl(var(--text)); }
        .welcome-fx .navcta { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; justify-content: flex-end; }

        /* Top-nav responsive scaling — keep visible (no burger), shrink & wrap instead */
        @media (max-width: 1180px) {
          .welcome-fx .container { padding: 0 24px; }
          .welcome-fx .navlinks { gap: 18px; font-size: 13.5px; }
          .welcome-fx .navcta { gap: 8px; }
          .welcome-fx .navcta .btn { padding: 8px 12px; font-size: 13px; }
        }
        @media (max-width: 960px) {
          .welcome-fx .top .container { row-gap: 10px; }
          .welcome-fx .navlinks { order: 3; flex-basis: 100%; gap: 14px; overflow-x: auto; flex-wrap: nowrap; padding-bottom: 4px; -webkit-overflow-scrolling: touch; scrollbar-width: thin; }
          .welcome-fx .navcta { order: 2; }
        }
        @media (max-width: 640px) {
          .welcome-fx .container { padding: 0 16px; }
          .welcome-fx .navlinks { font-size: 13px; gap: 12px; }
          .welcome-fx .navcta .btn { padding: 7px 10px; font-size: 12.5px; }
          .welcome-fx .navcta .btn span,
          .welcome-fx .navcta > span { display: none; }
        }
        .welcome-fx .btn { display: inline-flex; align-items: center; gap: 8px; padding: 11px 18px; border-radius: 10px; border: 1px solid hsl(var(--border)); background: transparent; color: hsl(var(--text)); font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 13.5px; font-weight: 500; letter-spacing: -0.01em; cursor: pointer; transition: all 0.15s ease; }
        .welcome-fx .btn:hover { border-color: hsl(var(--text) / 0.4); background: hsl(var(--text) / 0.05); color: hsl(var(--text)); }
        .welcome-fx .btn-primary { background: hsl(var(--text)); border-color: transparent; color: hsl(var(--bg)); font-weight: 600; }
        .welcome-fx .btn-primary:hover { filter: none; opacity: 0.85; background: hsl(var(--text)); color: hsl(var(--bg)); }
        .welcome-fx .pill { display: inline-block; padding: 4px 10px; border-radius: 999px; border: 1px solid hsl(var(--border)); background: hsl(var(--panel)); font-size: 12px; color: hsl(var(--muted)); }

        .welcome-fx .hero { position: relative; padding: 88px 0 74px; overflow: hidden; }
        .welcome-fx .hero::before { content: ""; position: absolute; inset: 0; background: radial-gradient(800px 400px at 20% 10%, hsl(var(--accent) / 0.18), transparent 60%), radial-gradient(700px 380px at 90% 30%, hsl(var(--accent) / 0.10), transparent 60%); pointer-events: none; }
        .welcome-fx .grid-bg { position: absolute; inset: 0; background-image: linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px); background-size: 48px 48px; mask-image: radial-gradient(ellipse at 50% 0%, black 30%, transparent 75%); opacity: 0.25; pointer-events: none; }
        .welcome-fx .eyebrow { display: inline-flex; align-items: center; gap: 8px; padding: 6px 12px; border: 1px solid hsl(var(--border)); border-radius: 999px; font-size: 12px; color: hsl(var(--muted)); background: hsl(var(--panel) / 0.4); }
        .welcome-fx .eyebrow .dot { width: 6px; height: 6px; border-radius: 50%; background: hsl(var(--good)); box-shadow: 0 0 10px hsl(var(--good)); }
        .welcome-fx .hero-grid { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(0, .80fr); gap: 30px; align-items: center; }
        @media (max-width: 1020px) { .welcome-fx .hero-grid { grid-template-columns: 1fr; gap: 28px; } }
        .welcome-fx .hero-copy { max-width: 840px; }
        .welcome-fx .hero-title { font-size: clamp(2rem, 4vw, 3.5rem); line-height: 0.95; letter-spacing: -0.05em; font-weight: 540; margin: 18px 0 14px; }
        .welcome-fx .grad { color: hsl(var(--accent)); }
        .welcome-fx .lede { max-width: 720px; font-size: 22px; line-height: 1.38; color: hsl(var(--text) / 0.9); letter-spacing: -0.015em; }
        .welcome-fx .hero-cta { display: flex; gap: 12px; margin-top: 24px; flex-wrap: wrap; }
        .welcome-fx .meta-row { display: flex; gap: 24px; flex-wrap: wrap; margin-top: 28px; color: hsl(var(--muted)); font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase; }
        .welcome-fx .meta-row span strong { color: hsl(var(--text)); font-weight: 600; }
        .welcome-fx .capabilities-strip { display: flex; flex-wrap: wrap; gap: 9px; margin-top: 24px; max-width: 860px; }
        .welcome-fx .cap-chip { display: inline-flex; align-items: center; padding: 8px 14px; border: 1px solid hsl(var(--border)); border-radius: 999px; background: hsl(var(--panel) / 0.68); font-size: 12px; letter-spacing: 0.04em; text-transform: uppercase; color: hsl(var(--text) / 0.9); }
        .welcome-fx .persona-paths { display: flex; flex-wrap: wrap; gap: 6px 18px; margin-top: 18px; padding-top: 16px; border-top: 1px solid hsl(var(--border)); }
        .welcome-fx .persona-paths a { display: inline-flex; align-items: center; gap: 6px; color: hsl(var(--muted)); font-size: 12.5px; font-weight: 550; transition: color .15s ease; }
        .welcome-fx .persona-paths a:hover { color: hsl(var(--accent)); }
        .welcome-fx .mid-cta {
          max-width: 1136px; margin: 0 auto; padding: 30px 32px;
          display: flex; align-items: center; justify-content: space-between; gap: 24px;
          border-top: 1px solid hsl(var(--border)); border-bottom: 1px solid hsl(var(--border));
          background: hsl(var(--panel) / 0.5);
        }
        .welcome-fx .mid-cta div { display: grid; gap: 5px; }
        .welcome-fx .mid-cta span {
          color: hsl(var(--accent)); font-family: 'IBM Plex Mono', ui-monospace, monospace;
          font-size: 10.5px; letter-spacing: .08em; text-transform: uppercase;
        }
        .welcome-fx .mid-cta strong { color: hsl(var(--text)); font-size: 17px; font-weight: 620; }
        @media (max-width: 720px) {
          .welcome-fx .mid-cta { margin: 0 16px; padding: 24px 0; align-items: flex-start; flex-direction: column; }
        }

        .welcome-fx .positioning-band { position: relative; overflow: hidden; padding: 0; border-top: 1px solid hsl(var(--border)); border-bottom: 1px solid hsl(var(--border)); background: linear-gradient(115deg, hsl(var(--panel) / 0.76), hsl(var(--bg)) 64%); }
        .welcome-fx .positioning-band::before { content: ""; position: absolute; inset: 0; pointer-events: none; opacity: .28; background-image: radial-gradient(circle, hsl(var(--accent) / .4) 1px, transparent 1px); background-size: 28px 28px; mask-image: linear-gradient(90deg, black, transparent 42%); }
        .welcome-fx .positioning-band::after { content: ""; position: absolute; width: 520px; height: 520px; left: max(-260px, calc(50% - 980px)); top: -270px; pointer-events: none; border: 1px solid hsl(var(--accent) / .12); border-radius: 50%; box-shadow: 0 0 0 72px hsl(var(--accent) / .025), 0 0 0 144px hsl(var(--accent) / .018); }
        .welcome-fx .positioning-inner { position: relative; display: grid; grid-template-columns: minmax(330px, .92fr) minmax(0, 1.58fr); }
        .welcome-fx .positioning-intro { display: flex; flex-direction: column; justify-content: center; padding: 72px 56px 72px 0; border-right: 1px solid hsl(var(--border)); }
        .welcome-fx .positioning-intro h2 { max-width: 560px; margin: 18px 0 18px; font-size: clamp(38px, 3.6vw, 56px); line-height: 1.01; font-weight: 580; letter-spacing: 0; }
        .welcome-fx .positioning-intro > p { max-width: 500px; color: hsl(var(--muted)); font-size: 16px; line-height: 1.65; }
        .welcome-fx .positioning-flow { display: grid; grid-template-columns: 1fr auto 1fr auto 1fr; align-items: center; gap: 10px; max-width: 500px; margin-top: 34px; padding-top: 22px; border-top: 1px solid hsl(var(--border)); }
        .welcome-fx .positioning-flow > div { display: flex; align-items: center; gap: 8px; min-width: 0; color: hsl(var(--text)); font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 11px; text-transform: uppercase; }
        .welcome-fx .positioning-flow svg { width: 16px; height: 16px; flex: none; color: hsl(var(--accent)); stroke-width: 1.7; }
        .welcome-fx .positioning-flow > svg { width: 14px; color: hsl(var(--muted) / .65); }
        .welcome-fx .positioning-principles { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); }
        .welcome-fx .positioning-principles article { position: relative; isolation: isolate; display: flex; min-width: 0; flex-direction: column; justify-content: center; overflow: hidden; padding: 72px 34px; border-right: 1px solid hsl(var(--border)); transition: background-color .35s ease, box-shadow .35s ease; }
        .welcome-fx .positioning-principles article::before { content: ""; position: absolute; top: 0; right: 0; left: 0; height: 2px; background: hsl(var(--accent)); transform: scaleX(0); transform-origin: left; transition: transform .3s ease; }
        .welcome-fx .positioning-principles article::after { content: ""; position: absolute; z-index: -1; inset: 0; pointer-events: none; opacity: 0; background: radial-gradient(260px 220px at 78% 78%, hsl(var(--accent) / .16), transparent 72%); transition: opacity .4s ease; }
        .welcome-fx .positioning-principles article:hover { background: hsl(var(--accent) / .035); box-shadow: inset 0 -1px 0 hsl(var(--accent) / .18); }
        .welcome-fx .positioning-principles article:hover::before { transform: scaleX(1); }
        .welcome-fx .positioning-principles article:hover::after { opacity: 1; }
        .welcome-fx .positioning-principles article:last-child { border-right: 0; }
        .welcome-fx .positioning-ghost { position: absolute; z-index: -1; right: -28px; bottom: -32px; width: 164px; height: 164px; color: hsl(var(--text)); opacity: .025; stroke-width: .55; transform: rotate(-7deg) scale(.94); transition: color .45s ease, opacity .45s ease, transform .55s cubic-bezier(.2,.8,.2,1); }
        .welcome-fx .positioning-principles article:hover .positioning-ghost { color: hsl(var(--accent)); opacity: .13; transform: rotate(0) scale(1.04) translate(-12px, -10px); }
        .welcome-fx .positioning-marker { display: flex; align-items: center; justify-content: space-between; margin-bottom: 34px; }
        .welcome-fx .positioning-marker svg { width: 26px; height: 26px; color: hsl(var(--muted)); stroke-width: 1.35; transition: color .25s ease, transform .25s ease; }
        .welcome-fx .positioning-marker span { color: hsl(var(--muted) / .65); font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 11px; }
        .welcome-fx .positioning-principles article:hover .positioning-marker svg { color: hsl(var(--accent)); transform: translateY(-3px); }
        .welcome-fx .positioning-kicker { color: hsl(var(--accent)); font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: .08em; text-transform: uppercase; }
        .welcome-fx .positioning-principles h3 { margin: 16px 0 14px; font-size: clamp(18px, 1.3vw, 22px); line-height: 1.25; font-weight: 620; letter-spacing: 0; }
        .welcome-fx .positioning-principles p { color: hsl(var(--muted)); font-size: 14px; line-height: 1.7; }
        @media (max-width: 980px) {
          .welcome-fx .positioning-inner { grid-template-columns: 1fr; }
          .welcome-fx .positioning-intro { padding: 56px 0; border-right: 0; border-bottom: 1px solid hsl(var(--border)); }
        }
        @media (max-width: 720px) {
          .welcome-fx .positioning-intro { padding: 44px 0; }
          .welcome-fx .positioning-intro h2 { font-size: 38px; }
          .welcome-fx .positioning-flow { gap: 7px; margin-top: 28px; }
          .welcome-fx .positioning-flow > div { gap: 5px; font-size: 9px; }
          .welcome-fx .positioning-flow svg { width: 14px; height: 14px; }
          .welcome-fx .positioning-principles { grid-template-columns: 1fr; }
          .welcome-fx .positioning-principles article { padding: 38px 0; border-right: 0; border-bottom: 1px solid hsl(var(--border)); }
          .welcome-fx .positioning-marker { margin-bottom: 26px; }
          .welcome-fx .positioning-principles article:last-child { border-bottom: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .welcome-fx .positioning-principles article,
          .welcome-fx .positioning-principles article::before,
          .welcome-fx .positioning-principles article::after,
          .welcome-fx .positioning-marker svg,
          .welcome-fx .positioning-ghost { transition: none; }
        }

        .welcome-fx .hero-diagram { position: relative; width: 100%; padding:0 0 65px 0; }

        .welcome-fx .hg-topbar { position: relative; z-index: 2; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 12px; }
        .welcome-fx .hg-title { font-size: 13px; font-weight: 600; color: hsl(var(--text)); }
        .welcome-fx .hg-live { display: inline-flex; align-items: center; gap: 6px; font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 10.5px; color: hsl(var(--good)); background: hsl(var(--good) / 0.12); border: 1px solid hsl(var(--good) / 0.25); padding: 4px 9px 4px 7px; border-radius: 999px; }
        .welcome-fx .hg-live-dot { width: 6px; height: 6px; border-radius: 50%; background: hsl(var(--good)); animation: hg-livepulse 2s infinite; }
        @keyframes hg-livepulse { 0% { box-shadow: 0 0 0 0 hsl(var(--good) / 0.45); } 70% { box-shadow: 0 0 0 6px hsl(var(--good) / 0); } 100% { box-shadow: 0 0 0 0 hsl(var(--good) / 0); } }

        .welcome-fx .hg-toolbar { position: relative; z-index: 2; display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 4px; }
        .welcome-fx .hg-chip { font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.02em; padding: 5px 10px; border-radius: 7px; border: 1px solid hsl(var(--border)); color: hsl(var(--muted)); background: hsl(var(--panel) / 0.5); }
        .welcome-fx .hg-chip.active { color: hsl(var(--accent-foreground)); background: hsl(var(--accent) / 1); border-color: hsl(var(--accent)); font-weight: 600; }

        .welcome-fx .hg-canvas { position: relative; z-index: 1; width: 100%; }
        .welcome-fx .hg-canvas > svg { width: 100%; height: auto; display: block; overflow: visible; }
        .welcome-fx .hg-flow-path { fill: none; stroke: url(#hgPathGrad); stroke-width: 1.6; }
        .welcome-fx .hg-flow-dot { fill: hsl(var(--accent)); filter: drop-shadow(0 0 4px hsl(var(--accent) / 0.75)); }
        .welcome-fx .hg-edge-label { font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 9px; letter-spacing: 0.02em; fill: hsl(var(--muted)); }
        .welcome-fx .hg-edge-bg { fill: hsl(var(--bg)); }
        .welcome-fx .hg-mesh-dot { fill: hsl(var(--accent)); }
        .welcome-fx .hg-mesh-line { stroke: hsl(var(--accent)); stroke-width: 1; }

        .welcome-fx .hg-core-frame { fill: hsl(var(--text)); stroke: hsl(var(--accent)); stroke-width: 1.5; }
        .welcome-fx .hg-core-tick { fill: none; stroke: hsl(var(--accent)); stroke-width: 1; }
        .welcome-fx .hg-core-axis { fill: none; stroke: hsl(var(--bg) / .14); stroke-width: 1; }
        .welcome-fx .hg-core-point { fill: hsl(var(--accent)); }
        .welcome-fx .hg-core-ic { fill: none; stroke: hsl(var(--bg)); stroke-width: 1.6; stroke-linecap: round; stroke-linejoin: round; opacity: 0.95; }
        .welcome-fx .hg-core-label { fill: hsl(var(--bg)); font-size: 12px; font-weight: 600; letter-spacing: -0.01em; }
        .welcome-fx .hg-caption { fill: hsl(var(--muted)); font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.09em; text-transform: uppercase; }

        .welcome-fx .hg-node { transition: transform 0.25s ease; }
        .welcome-fx .hg-node:hover { transform: translateY(-2px); }
        .welcome-fx .hg-node-box { fill: hsl(var(--panel)); stroke: hsl(var(--border)); stroke-width: 1; filter: drop-shadow(0 12px 26px rgba(0,0,0,0.4)); transition: stroke 0.2s ease; }
        .welcome-fx .hg-node:hover .hg-node-box { stroke: hsl(var(--accent) / 0.7); }
        .welcome-fx .hg-node-iconbox { fill: hsl(var(--accent) / 1); }
        .welcome-fx .hg-node-ic { fill: none; stroke: hsl(var(--accent-foreground)); stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
        .welcome-fx .hg-node-name { fill: hsl(var(--text)); font-size: 14px; font-weight: 600; }
        .welcome-fx .hg-node-sub { fill: hsl(var(--muted)); font-size: 11.5px; }

        .welcome-fx .hg-stats { position: relative; z-index: 2; display: flex; flex-wrap: wrap; gap: 22px; margin-top: 12px; padding-top: 14px; border-top: 1px solid hsl(var(--border)); font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 11px; color: hsl(var(--muted)); }
        .welcome-fx .hg-stats b { color: hsl(var(--text)); font-weight: 600; }
        @media (max-width: 1020px) {
          .welcome-fx .hero-diagram { max-width: 620px; margin: 0 auto; }
          .welcome-fx .lede { font-size: 19px; }
        }
        @media (max-width: 480px) {
          .welcome-fx .hg-stats { gap: 14px; }
        }
        @media (max-width: 640px) {
          .welcome-fx .hero { padding: 48px 0 40px; }
          .welcome-fx section { padding: 56px 0; }
          .welcome-fx .hero-cta { gap: 8px; }
          .welcome-fx .hero-cta .btn { padding: 9px 14px; font-size: 13.5px; }
          .welcome-fx .meta-row { gap: 14px; font-size: 11px; }
          .welcome-fx .lede { font-size: 16px; }
          .welcome-fx .capabilities-strip { gap: 8px; }
          .welcome-fx .cap-chip { font-size: 11px; padding: 7px 11px; }
          .welcome-fx .section-head { margin-bottom: 32px; gap: 16px; }
        }

        .welcome-fx .hero-proof { padding-top: 72px; }

        .welcome-fx .hero-art { margin-top: 56px; border: 1px solid hsl(var(--border)); border-radius: 14px; background: hsl(var(--panel)); box-shadow: 0 30px 80px rgba(0,0,0,0.45); overflow: hidden; }
        .welcome-fx .term-bar { display: flex; align-items: center; gap: 6px; padding: 10px 14px; border-bottom: 1px solid hsl(var(--border)); background: hsl(var(--bg) / 0.6); }
        .welcome-fx .term-bar i { width: 11px; height: 11px; border-radius: 50%; display: inline-block; background: hsl(var(--bad)); }
        .welcome-fx .term-bar i:nth-child(2) { background: hsl(var(--warn)); }
        .welcome-fx .term-bar i:nth-child(3) { background: hsl(var(--good)); }
        .welcome-fx .term-bar span { margin-left: 14px; color: hsl(var(--muted)); font-size: 12px; font-family: 'IBM Plex Mono', ui-monospace, monospace; }
        .welcome-fx .term-grid { display: grid; grid-template-columns: 1.2fr 1fr; gap: 0; }
        @media (max-width: 900px) { .welcome-fx .term-grid { grid-template-columns: 1fr; } }
        .welcome-fx .term-pane { padding: 18px 20px; font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 12.5px; line-height: 1.7; color: hsl(var(--text)); margin: 0; white-space: pre-wrap; }
        .welcome-fx .term-pane.left { border-right: 1px solid hsl(var(--border)); }
        .welcome-fx .trace-line { display: flex; justify-content: space-between; color: hsl(var(--muted)); padding: 4px 0; }
        .welcome-fx .trace-line code { color: hsl(var(--text)); }

        .welcome-fx section { padding: 96px 0; border-top: 1px solid hsl(var(--border)); }
        .welcome-fx .section-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 32px; margin-bottom: 48px; flex-wrap: wrap; }
        .welcome-fx .section-eyebrow { display: flex; align-items: center; gap: 12px; font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 12px; color: hsl(var(--accent)); letter-spacing: 0.18em; text-transform: uppercase; font-weight: 600; }
        .welcome-fx .section-eyebrow::before { content: ""; width: 30px; height: 1px; flex: 0 0 auto; background: hsl(var(--accent)); }
        .welcome-fx h2 { font-size: clamp(40px, 5vw, 84px); line-height: 0.96; letter-spacing: -0.035em; margin: 8px 0; font-weight: 500; max-width: 1000px; }
        .welcome-fx .section-sub { color: hsl(var(--muted)); max-width: 560px; font-size: 16px; line-height: 1.6; }

        .welcome-fx .twocol { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
        @media (max-width: 900px) { .welcome-fx .twocol { grid-template-columns: 1fr; } }
        .welcome-fx .why-head h2 { max-width: 800px; }
        /* Comparison ledger — a single framed table that reads dimension by
           dimension, far more deliberate than two bullet cards. */
        .welcome-fx .ledger { border: 1px solid hsl(var(--border)); border-radius: 16px; overflow: hidden; background: linear-gradient(180deg, hsl(var(--panel)), hsl(var(--bg) / 0.46)); }
        .welcome-fx .ledger-head, .welcome-fx .ledger-row { display: grid; grid-template-columns: 200px 1fr 1fr; align-items: stretch; }
        .welcome-fx .ledger-head { background: hsl(var(--bg) / 0.5); border-bottom: 1px solid hsl(var(--border)); }
        .welcome-fx .ledger-h { padding: 16px 22px; font-size: 12px; letter-spacing: 0.06em; }
        .welcome-fx .ledger-h-dim { color: hsl(var(--muted)); text-transform: uppercase; letter-spacing: 0.16em; align-self: center; }
        .welcome-fx .ledger-tag { display: inline-flex; align-items: center; gap: 8px; border-radius: 999px; padding: 6px 12px; font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; border: 1px solid hsl(var(--border)); }
        .welcome-fx .ledger-tag.bad { color: hsl(var(--bad)); border-color: hsl(var(--bad) / 0.4); background: hsl(var(--bad) / 0.07); }
        .welcome-fx .ledger-tag.good { color: hsl(var(--good)); border-color: hsl(var(--good) / 0.45); background: hsl(var(--good) / 0.08); }
        .welcome-fx .ledger-row { border-top: 1px solid hsl(var(--border)); transition: background 0.2s ease; }
        .welcome-fx .ledger-row:first-of-type { border-top: none; }
        .welcome-fx .ledger-row:hover { background: hsl(var(--accent) / 0.04); }
        .welcome-fx .ledger-dim { display: flex; align-items: center; gap: 10px; padding: 20px 22px; font-size: 14.5px; font-weight: 600; letter-spacing: -0.01em; border-right: 1px solid hsl(var(--border)); }
        .welcome-fx .ledger-dim-ic { display: grid; place-items: center; width: 30px; height: 30px; border-radius: 8px; color: hsl(var(--accent-foreground)); background: hsl(var(--accent) / 1); border: 1px solid hsl(var(--accent)); flex-shrink: 0; }
        .welcome-fx .ledger-cell { padding: 20px 22px; font-size: 14px; line-height: 1.5; color: hsl(var(--text) / 0.84); position: relative; }
        .welcome-fx .ledger-cell.bad { border-right: 1px solid hsl(var(--border)); padding-left: 34px; }
        .welcome-fx .ledger-cell.good { padding-left: 34px; }
        .welcome-fx .ledger-cell::before { content: ""; position: absolute; left: 22px; top: 25px; width: 7px; height: 7px; border-radius: 50%; }
        .welcome-fx .ledger-cell.bad::before { background: hsl(var(--bad)); }
        .welcome-fx .ledger-cell.good::before { background: hsl(var(--good)); }
        @media (max-width: 820px) {
          .welcome-fx .ledger-head { display: none; }
          .welcome-fx .ledger-row { grid-template-columns: 1fr; }
          .welcome-fx .ledger-dim { border-right: none; border-bottom: 1px dashed hsl(var(--border)); padding-bottom: 12px; }
          .welcome-fx .ledger-cell.bad { border-right: none; }
        }
        .welcome-fx .why-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
        @media (max-width: 900px) { .welcome-fx .why-grid { grid-template-columns: 1fr; } }
        .welcome-fx .why-card { border: 1px solid hsl(var(--border)); border-radius: 14px; background: linear-gradient(180deg, hsl(var(--panel)), hsl(var(--bg) / 0.46)); padding: 22px 22px 18px; }
        .welcome-fx .why-card-kicker { display: inline-flex; align-items: center; border-radius: 999px; border: 1px solid hsl(var(--border)); padding: 5px 10px; font-size: 10.5px; letter-spacing: 0.14em; text-transform: uppercase; color: hsl(var(--muted)); margin-bottom: 12px; }
        .welcome-fx .why-card h3 { margin: 0 0 10px; font-size: 20px; letter-spacing: -0.015em; }
        .welcome-fx .why-card ul { margin: 0; padding: 0; list-style: none; }
        .welcome-fx .why-card li { position: relative; padding: 10px 0 10px 16px; border-top: 1px dashed hsl(var(--border)); color: hsl(var(--text) / 0.84); font-size: 14px; line-height: 1.45; }
        .welcome-fx .why-card li:first-child { border-top: none; }
        .welcome-fx .why-card li::before { content: ""; position: absolute; left: 0; top: 16px; width: 6px; height: 6px; border-radius: 50%; }
        .welcome-fx .why-card.legacy { border-color: hsl(var(--bad) / 0.3); }
        .welcome-fx .why-card.legacy li::before { background: hsl(var(--bad)); }
        .welcome-fx .why-card.target { border-color: hsl(var(--good) / 0.35); background: linear-gradient(180deg, hsl(var(--good) / 0.07), hsl(var(--panel))); }
        .welcome-fx .why-card.target li::before { background: hsl(var(--good)); }
        .welcome-fx .why-strip { margin-top: 14px; display: flex; flex-wrap: wrap; gap: 8px; }
        .welcome-fx .why-pill { display: inline-flex; align-items: center; border-radius: 7px; border: 1px solid hsl(var(--accent)); background: hsl(var(--accent) / 1); padding: 7px 12px; font-size: 11.5px; color: hsl(var(--accent-foreground)); }

        .welcome-fx .panel { border: 1px solid hsl(var(--border)); background: linear-gradient(180deg, hsl(var(--panel)), hsl(var(--bg) / 0.45)); border-radius: 14px; padding: 24px; transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease; }
        .welcome-fx .panel:hover { transform: translateY(-2px); border-color: hsl(var(--accent) / 0.28); box-shadow: 0 12px 32px hsl(var(--accent) / 0.08); }
        .welcome-fx .panel.bad { border-color: hsl(var(--bad) / 0.3); }
        .welcome-fx .panel.good { border-color: hsl(var(--good) / 0.35); background: linear-gradient(180deg, hsl(var(--good) / 0.05), hsl(var(--panel))); }
        .welcome-fx .panel h3 { margin: 0 0 14px; font-size: 18px; }
        .welcome-fx .panel ul { margin: 0; padding: 0; list-style: none; }
        .welcome-fx .panel li { padding: 10px 0; border-top: 1px dashed hsl(var(--border)); font-size: 14.5px; color: hsl(var(--text) / 0.85); display: flex; gap: 10px; align-items: flex-start; }
        .welcome-fx .panel li:first-child { border-top: none; padding-top: 0; }
        .welcome-fx .panel li::before { content: ""; display: inline-block; width: 6px; height: 6px; border-radius: 50%; margin-top: 8px; flex-shrink: 0; background: hsl(var(--muted)); }
        .welcome-fx .panel.bad li::before { background: hsl(var(--bad)); }
        .welcome-fx .panel.good li::before { background: hsl(var(--good)); }

        .welcome-fx .feat-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
        @media (max-width: 980px) { .welcome-fx .feat-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 640px) { .welcome-fx .feat-grid { grid-template-columns: 1fr; } }
        .welcome-fx .feat { position: relative; border: 1px solid hsl(var(--border)); border-radius: 14px; background: linear-gradient(185deg, hsl(var(--panel)) 0%, hsl(var(--bg) / 0.52) 100%); padding: 24px; transition: transform 0.24s ease, border-color 0.24s ease, box-shadow 0.24s ease; overflow: hidden; }
        .welcome-fx .feat::after { content: ""; position: absolute; inset: -1px; background: radial-gradient(420px 110px at 15% -10%, hsl(var(--accent) / 0.16), transparent 65%); opacity: 0.7; pointer-events: none; }
        .welcome-fx .feat:hover { transform: translateY(-4px); border-color: hsl(var(--accent) / 0.48); box-shadow: 0 18px 40px hsl(var(--accent) / 0.14); }
        .welcome-fx .feat .ic { width: 38px; height: 38px; display: grid; place-items: center; border-radius: 8px; background: hsl(var(--accent) / 1); color: hsl(var(--accent-foreground)); margin-bottom: 14px; border: 1px solid hsl(var(--accent)); box-shadow: 0 4px 16px hsl(var(--accent) / 0.18); }
        .welcome-fx .feat h3 { margin: 0 0 8px; font-size: 17px; font-weight: 600; letter-spacing: -0.01em; }
        .welcome-fx .feat p { margin: 0; color: hsl(var(--muted)); font-size: 14.5px; line-height: 1.55; }

        /* Objects grid (Foundry-inspired) */
        .welcome-fx .obj-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; border: 1px solid hsl(var(--border)); border-radius: 14px; overflow: hidden; }
        @media (max-width: 980px) { .welcome-fx .obj-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 600px) { .welcome-fx .obj-grid { grid-template-columns: 1fr; } }
        .welcome-fx .obj { padding: 28px; border-right: 1px solid hsl(var(--border)); border-bottom: 1px solid hsl(var(--border)); background: linear-gradient(180deg, hsl(var(--panel)), hsl(var(--bg) / 0.48)); transition: background 0.2s ease, border-color 0.2s ease; }
        .welcome-fx .obj:hover { background: linear-gradient(180deg, hsl(var(--panel)), hsl(var(--accent) / 0.06)); border-color: hsl(var(--accent) / 0.3); }
        .welcome-fx .obj:last-child { border-right: none; }
        @media (max-width: 980px) {
          .welcome-fx .obj:nth-child(2n) { border-right: none; }
          .welcome-fx .obj:nth-child(n+3) { border-bottom: none; }
        }
        .welcome-fx .obj-num { font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 12px; color: hsl(var(--accent)); letter-spacing: 0.1em; margin-bottom: 18px; }
        .welcome-fx .obj h3 { margin: 0 0 8px; font-size: 22px; font-weight: 500; letter-spacing: -0.02em; }
        .welcome-fx .obj p { margin: 0; color: hsl(var(--muted)); font-size: 14px; line-height: 1.55; }

        /* Action lifecycle — a connected execution rail, not four generic cards. */
        .welcome-fx #action { background: radial-gradient(ellipse 60% 60% at 88% 34%, hsl(var(--accent) / .055), transparent 75%), hsl(var(--bg)); }
        .welcome-fx .action-showcase { display: grid; grid-template-columns: minmax(0, .8fr) minmax(0, 1.2fr); align-items: center; gap: clamp(28px, 4vw, 64px); padding-bottom: clamp(46px, 6vw, 84px); }
        .welcome-fx .action-story { min-width: 0; padding-left: clamp(16px, 2vw, 26px); border-left: 1px solid hsl(var(--border)); }
        .welcome-fx .action-story h2 { max-width: 11ch; margin: 22px 0 26px; font-family: 'IBM Plex Sans', sans-serif; font-weight: 500; letter-spacing: -.04em; font-size: clamp(4.5rem, 6vw, 7.2rem); line-height: .91; }
        .welcome-fx .action-story h2 em { color: hsl(var(--accent)); font-weight: 400; }
        .welcome-fx .action-lede { max-width: 38ch; margin: 0; padding-left: 16px; border-left: 2px solid hsl(var(--accent)); color: hsl(var(--text)); font-size: clamp(17px, 1.5vw, 21px); line-height: 1.45; }
        .welcome-fx .action-support { max-width: 50ch; margin: 18px 0 0 18px; color: hsl(var(--muted)); font-size: 14px; line-height: 1.65; }
        .welcome-fx .action-cta { display: flex; flex-wrap: wrap; gap: 0; margin-top: 28px; }
        .welcome-fx .action-cta .btn { justify-content: center; min-height: 48px; border-radius: 0; font-size: 10px; text-transform: uppercase; letter-spacing: .06em; }
        .welcome-fx .action-cta .btn + .btn { margin-left: -1px; }
        .welcome-fx .action-story-foot { margin-top: 26px; padding-top: 15px; border-top: 1px solid hsl(var(--border)); color: hsl(var(--muted)); font: 9px/1.5 'IBM Plex Mono', ui-monospace, monospace; letter-spacing: .06em; text-transform: uppercase; }
        .welcome-fx .action-story-foot span { margin: 0 5px; color: hsl(var(--accent)); }
        .welcome-fx .action-video { min-width: 0; }
        .welcome-fx .action-video .hv { box-shadow: 0 28px 64px -42px hsl(0 0% 0% / .48); }
        .welcome-fx .action-sequence-head { display: flex; justify-content: space-between; gap: 16px; padding: 0 0 14px; color: hsl(var(--muted)); font: 10px/1.5 'IBM Plex Mono', ui-monospace, monospace; letter-spacing: .12em; text-transform: uppercase; }
        .welcome-fx .action-sequence-head span:last-child { color: hsl(var(--accent)); }
        @media (max-width: 1040px) { .welcome-fx .action-showcase { grid-template-columns: 1fr; } .welcome-fx .action-story h2 { max-width: none; } .welcome-fx .action-video { max-width: 820px; } }
        @media (max-width: 600px) { .welcome-fx .action-story h2 { font-size: clamp(4.1rem, 17vw, 5.5rem); } .welcome-fx .action-cta { display: grid; } .welcome-fx .action-cta .btn + .btn { margin: -1px 0 0; } .welcome-fx .action-story-foot { font-size: 8px; } }
        .welcome-fx .action-kicker { display: flex; align-items: center; gap: 12px; color: hsl(var(--muted)); font-size: 13px; font-weight: 560; }
        .welcome-fx .action-kicker::before { content: ""; width: 30px; height: 1px; background: hsl(var(--accent)); }
        .welcome-fx .action-kicker b { font-family: 'IBM Plex Mono', ui-monospace, monospace; color: hsl(var(--accent)); font-size: 10px; letter-spacing: .08em; }
        .welcome-fx .act-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; border-top: 1px solid hsl(var(--border)); border-bottom: 1px solid hsl(var(--border)); }
        .welcome-fx .act-step { position: relative; min-width: 0; padding: 26px 24px 24px; border-right: 1px solid hsl(var(--border)); }
        .welcome-fx .act-step:last-child { border-right: 0; }
        .welcome-fx .act-step-no { display: inline-grid; place-items: center; width: 28px; height: 28px; margin-bottom: 18px; border: 1px solid hsl(var(--accent)); border-radius: 4px; background: hsl(var(--accent) / 1); color: hsl(var(--accent-foreground)); font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 10px; }
        .welcome-fx .act-step h3 { margin: 0 0 8px; font-size: 17px; font-weight: 620; }
        .welcome-fx .act-step p { margin: 0; color: hsl(var(--muted)); font-size: 14px; line-height: 1.55; }
        @media (max-width: 980px) { .welcome-fx .act-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 980px) {
          .welcome-fx .act-step:nth-child(2) { border-right: 0; }
          .welcome-fx .act-step:nth-child(-n+2) { border-bottom: 1px solid hsl(var(--border)); }
        }
        @media (max-width: 600px) {
          .welcome-fx .act-grid { grid-template-columns: 1fr; }
          .welcome-fx .act-step { border-right: 0; border-bottom: 1px solid hsl(var(--border)); }
          .welcome-fx .act-step:last-child { border-bottom: 0; }
        }

        .welcome-fx .arch { position: relative; isolation: isolate; border: 1px solid hsl(var(--border)); border-radius: 16px; background: linear-gradient(180deg, hsl(var(--panel)) 0%, hsl(var(--bg) / 0.52) 100%); padding: 28px; display: grid; gap: 16px; overflow: hidden; }
        .welcome-fx .arch::before { content: ""; position: absolute; inset: 0; background: radial-gradient(760px 180px at 50% -10%, hsl(var(--accent) / 0.14), transparent 72%); pointer-events: none; }
        .welcome-fx .arch-links { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; z-index: 0; }
        .welcome-fx .arch-link-base path { fill: none; stroke: url(#archLinkStroke); stroke-width: 1.25; stroke-linecap: round; }
        .welcome-fx .arch-link-trace path { fill: none; stroke: hsl(var(--accent)); stroke-width: 1.8; stroke-linecap: round; stroke-dasharray: 16 72; stroke-dashoffset: 72; filter: drop-shadow(0 0 6px hsl(var(--accent) / 0.82)); animation: arch-flow 4.6s linear infinite; }
        .welcome-fx .arch-stage { justify-self: center; border-radius: 999px; border: 1px solid hsl(var(--border)); background: hsl(var(--bg) / 0.55); padding: 5px 12px; font-size: 10.5px; letter-spacing: 0.14em; text-transform: uppercase; color: hsl(var(--muted)); }
        .welcome-fx .arch-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
        .welcome-fx .arch-row, .welcome-fx .arch-stage { position: relative; z-index: 1; }
        @media (max-width: 900px) { .welcome-fx .arch-row { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 900px) { .welcome-fx .arch-links { display: none; } }
        .welcome-fx .node { position: relative; border: 1px solid hsl(var(--border)); background: linear-gradient(180deg, hsl(var(--panel)) 0%, hsl(var(--panel) / 0.96) 100%); border-radius: 10px; padding: 14px; text-align: center; font-size: 13.5px; transition: transform 0.24s ease, border-color 0.24s ease, box-shadow 0.24s ease, background 0.24s ease; }
        .welcome-fx .node:hover { transform: translateY(-2px); border-color: hsl(var(--accent) / 0.38); box-shadow: 0 10px 28px hsl(var(--accent) / 0.12); }
        .welcome-fx .node.kernel, .welcome-fx .node.gov { border-color: hsl(var(--accent)); background: hsl(var(--accent) / 1); color: hsl(var(--accent-foreground)); }
        .welcome-fx .node.kernel { font-weight: 600; box-shadow: 0 8px 24px hsl(var(--accent) / 0.2); }
        .welcome-fx .node-head { display: inline-flex; align-items: center; justify-content: center; gap: 7px; font-weight: 590; letter-spacing: -0.01em; }
        .welcome-fx .node small { display: block; color: hsl(var(--muted)); font-size: 11.5px; margin-top: 4px; }
        .welcome-fx .node.kernel small, .welcome-fx .node.gov small { color: hsl(var(--accent-foreground) / 0.78); }
        .welcome-fx .pack-head h2 { max-width: 980px; }
        .welcome-fx .orb-head h2 { max-width: 900px; }
        .welcome-fx .pack-ladder { display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; margin: -4px 0 18px; }
        .welcome-fx .pack-ladder-step { display: inline-flex; align-items: center; gap: 10px; padding: 8px 12px; border-radius: 8px; border: 1px solid hsl(var(--border)); background: hsl(var(--panel) / 0.66); color: hsl(var(--text) / 0.92); font-size: 12px; letter-spacing: 0.04em; text-transform: uppercase; }
        .welcome-fx .pack-ladder-step i { font-style: normal; color: hsl(var(--accent)); font-family: 'IBM Plex Mono', ui-monospace, monospace; }
        .welcome-fx .pack-top-grid { display: grid; grid-template-columns: 1.18fr 0.82fr; gap: 14px; margin-bottom: 16px; }
        .welcome-fx .pack-top-grid.deploy-grid { margin-top: 16px; }
        @media (max-width: 980px) {
          .welcome-fx .pack-top-grid { grid-template-columns: 1fr; }
        }
        .welcome-fx .pack-why,
        .welcome-fx .orb-hero,
        .welcome-fx .pack-deploy,
        .welcome-fx .pack-consulting,
        .welcome-fx .pack-library { border-radius: 8px; }
        .welcome-fx .pack-why p,
        .welcome-fx .orb-hero p,
        .welcome-fx .pack-deploy p,
        .welcome-fx .pack-consulting p,
        .welcome-fx .pack-library p { margin: 10px 0 0; color: hsl(var(--muted)); font-size: 14px; line-height: 1.6; }
        .welcome-fx .pack-include-grid { margin-top: 14px; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
        .welcome-fx .pack-include-grid span { position: relative; border: 1px solid hsl(var(--border)); border-radius: 10px; background: hsl(var(--bg) / 0.42); padding: 9px 10px 9px 22px; font-size: 12.5px; color: hsl(var(--text) / 0.9); line-height: 1.35; }
        .welcome-fx .pack-include-grid span::before { content: ""; position: absolute; left: 10px; top: 14px; width: 6px; height: 6px; border-radius: 50%; background: hsl(var(--good)); }
        .welcome-fx .pack-include-grid.compact { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        @media (max-width: 760px) {
          .welcome-fx .pack-include-grid,
          .welcome-fx .pack-include-grid.compact { grid-template-columns: 1fr; }
        }
        .welcome-fx .orb-hero { border: 1px solid hsl(var(--accent) / 0.42); background: radial-gradient(430px 210px at 85% -5%, hsl(var(--accent) / 0.2), transparent 62%), linear-gradient(180deg, hsl(var(--panel)) 0%, hsl(var(--bg) / 0.48) 100%); padding: 22px; }
        .welcome-fx .orb-hero h3 { margin: 12px 0 0; font-size: 28px; line-height: 1.06; letter-spacing: -0.02em; }
        .welcome-fx .orb-checks { margin-top: 14px; display: flex; flex-wrap: wrap; gap: 8px; }
        .welcome-fx .orb-checks span { display: inline-flex; align-items: center; padding: 6px 11px; border-radius: 7px; border: 1px solid hsl(var(--accent)); background: hsl(var(--accent) / 1); color: hsl(var(--accent-foreground)); font-size: 12px; }
        .welcome-fx .orb-footer { margin-top: 14px; font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: hsl(var(--accent)); }
        .welcome-fx .pack-card { border: 1px solid hsl(var(--border)); border-radius: 8px; background: linear-gradient(180deg, hsl(var(--panel)) 0%, hsl(var(--bg) / 0.48) 100%); padding: 24px; display: grid; grid-template-columns: 1.12fr 0.88fr; gap: 18px; align-items: stretch; }
        @media (max-width: 980px) { .welcome-fx .pack-card { grid-template-columns: 1fr; } }
        .welcome-fx .pack-main { border: 1px solid hsl(var(--border)); border-radius: 8px; background: hsl(var(--bg) / 0.4); padding: 20px; }
        .welcome-fx .pack-kicker { display: inline-flex; border-radius: 7px; border: 1px solid hsl(var(--accent)); background: hsl(var(--accent) / 1); color: hsl(var(--accent-foreground)); padding: 5px 10px; font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; }
        .welcome-fx .pack-title { margin: 12px 0 8px; font-size: 31px; line-height: 1.05; letter-spacing: -0.03em; }
        .welcome-fx .pack-desc { margin: 0; color: hsl(var(--muted)); font-size: 14px; line-height: 1.55; max-width: 56ch; }
        .welcome-fx .pack-metrics { margin-top: 14px; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
        .welcome-fx .pack-metrics > div { border: 1px solid hsl(var(--border)); border-radius: 10px; background: hsl(var(--panel) / 0.72); padding: 10px; display: grid; gap: 2px; }
        .welcome-fx .pack-metrics strong { font-size: 18px; line-height: 1; color: hsl(var(--text)); }
        .welcome-fx .pack-metrics span { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: hsl(var(--muted)); }
        .welcome-fx .pack-badges { display: flex; gap: 8px; margin-top: 14px; flex-wrap: wrap; }
        .welcome-fx .pack-detail-tabs { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-bottom: 12px; }
        .welcome-fx .pack-detail-tab {
          appearance: none;
          width: 100%;
          min-height: 74px;
          border: 1px solid hsl(var(--border));
          border-radius: 8px;
          background: linear-gradient(180deg, hsl(var(--panel) / 0.9) 0%, hsl(var(--bg) / 0.55) 100%);
          color: hsl(var(--muted));
          font-family: 'IBM Plex Mono', ui-monospace, monospace;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          line-height: 1.18;
          padding: 12px 10px;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          transition: border-color 0.18s ease, color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
        }
        .welcome-fx .pack-detail-tab::before {
          content: "";
          position: absolute;
          inset: 0 0 auto 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, hsl(var(--accent) / 0.75), transparent);
          opacity: 0;
          transition: opacity 0.18s ease;
        }
        .welcome-fx .pack-detail-tab:hover {
          border-color: hsl(var(--accent) / 0.35);
          color: hsl(var(--text));
          transform: translateY(-1px);
          box-shadow: 0 10px 24px hsl(var(--accent) / 0.08);
        }
        .welcome-fx .pack-detail-tab:focus-visible {
          outline: 2px solid hsl(var(--accent) / 0.7);
          outline-offset: 2px;
        }
        .welcome-fx .pack-detail-tab:active {
          transform: translateY(0);
        }
        .welcome-fx .pack-detail-tab.on {
          border-color: hsl(var(--accent));
          color: hsl(var(--accent-foreground));
          background: hsl(var(--accent) / 1);
          box-shadow: 0 14px 28px hsl(var(--accent) / 0.2);
        }
        .welcome-fx .pack-detail-tab.on::before { opacity: 1; }
        @media (max-width: 640px) {
          .welcome-fx .pack-detail-tab {
            min-height: 68px;
            font-size: 10px;
            letter-spacing: 0.08em;
            padding: 10px 8px;
          }
        }
        .welcome-fx .pack-includes { margin-top: 14px; }
        .welcome-fx .pack-includes h4,
        .welcome-fx .pack-side-card h4 { margin: 0 0 10px; font-size: 12px; color: hsl(var(--muted)); letter-spacing: 0.14em; text-transform: uppercase; }
        .welcome-fx .pack-uses { display: grid; gap: 10px; }
        .welcome-fx .pack-side-card { border: 1px solid hsl(var(--border)); border-radius: 8px; background: hsl(var(--bg) / 0.38); padding: 14px; }
        .welcome-fx .pack-readiness { display: grid; gap: 8px; }
        .welcome-fx .pack-readiness-row { display: flex; justify-content: space-between; gap: 10px; padding: 8px 0; border-top: 1px dashed hsl(var(--border)); }
        .welcome-fx .pack-readiness-row:first-child { border-top: none; padding-top: 0; }
        .welcome-fx .pack-readiness-row span { color: hsl(var(--muted)); font-size: 12.5px; }
        .welcome-fx .pack-readiness-row strong { color: hsl(var(--text)); font-size: 12.5px; font-weight: 600; }
        .welcome-fx .uc { display: flex; justify-content: space-between; gap: 14px; padding: 10px 12px; border: 1px solid hsl(var(--border)); border-radius: 10px; background: hsl(var(--bg) / 0.4); font-size: 13.5px; transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease; }
        .welcome-fx .uc + .uc { margin-top: 8px; }
        .welcome-fx .uc:hover { transform: translateX(3px); border-color: hsl(var(--accent) / 0.35); box-shadow: 0 8px 20px hsl(var(--accent) / 0.1); }
        .welcome-fx .uc span { display: grid; gap: 3px; text-align: left; }
        .welcome-fx .uc span strong { font-size: 13.5px; font-weight: 600; letter-spacing: -0.01em; }
        .welcome-fx .uc span em { font-style: normal; font-size: 11px; color: hsl(var(--muted)); letter-spacing: 0.01em; line-height: 1.45; }
        .welcome-fx .uc code { color: hsl(var(--accent)); font-size: 12px; font-family: 'IBM Plex Mono', ui-monospace, monospace; }
        .welcome-fx .pack-flow { margin-top: 14px; border: 1px solid hsl(var(--border)); border-radius: 12px; background: hsl(var(--panel) / 0.6); padding: 10px; display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 7px; }
        .welcome-fx .pack-flow-step { display: inline-flex; align-items: center; gap: 7px; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: hsl(var(--text) / 0.9); }
        .welcome-fx .pack-flow-step i { font-style: normal; color: hsl(var(--accent)); font-family: 'IBM Plex Mono', ui-monospace, monospace; }
        .welcome-fx .pack-targets { margin-top: 12px; display: flex; flex-wrap: wrap; gap: 8px; }
        .welcome-fx .pack-targets span { border: 1px solid hsl(var(--border)); border-radius: 999px; padding: 6px 10px; font-size: 12px; color: hsl(var(--text) / 0.9); background: hsl(var(--bg) / 0.4); }
        .welcome-fx .orb-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        .welcome-fx .orb-panel { border: 1px solid hsl(var(--border)); border-radius: 8px; background: radial-gradient(420px 180px at 85% -10%, hsl(var(--accent) / 0.16), transparent 65%), linear-gradient(180deg, hsl(var(--panel)) 0%, hsl(var(--bg) / 0.46) 100%); padding: 22px; }
        .welcome-fx .orb-panel h3 { margin: 0; font-size: 30px; line-height: 1.06; letter-spacing: -0.02em; }
        .welcome-fx .orb-panel h4 { margin: 0; font-size: 12px; color: hsl(var(--muted)); letter-spacing: 0.14em; text-transform: uppercase; }
        .welcome-fx .orb-panel p { margin: 12px 0 0; color: hsl(var(--muted)); font-size: 14px; line-height: 1.6; }
        @media (max-width: 980px) { .welcome-fx .orb-grid { grid-template-columns: 1fr; } }
        .welcome-fx .pack-compare { margin-top: 16px; border: 1px solid hsl(var(--border)); border-radius: 13px; overflow: hidden; background: linear-gradient(180deg, hsl(var(--panel)), hsl(var(--bg) / 0.52)); }
        .welcome-fx .pack-compare-head,
        .welcome-fx .pack-compare-row { display: grid; grid-template-columns: 1fr 1fr; }
        .welcome-fx .pack-compare-head { background: hsl(var(--bg) / 0.54); border-bottom: 1px solid hsl(var(--border)); }
        .welcome-fx .pack-compare-head span { padding: 12px 16px; font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; color: hsl(var(--muted)); }
        .welcome-fx .pack-compare-row { border-top: 1px solid hsl(var(--border)); }
        .welcome-fx .pack-compare-row span { padding: 13px 16px; font-size: 13px; line-height: 1.5; color: hsl(var(--text) / 0.88); }
        .welcome-fx .pack-compare-row span b { color: hsl(var(--text)); }
        .welcome-fx .pack-compare-row span:first-child,
        .welcome-fx .pack-compare-head span:first-child { border-right: 1px solid hsl(var(--border)); }
        @media (max-width: 760px) {
          .welcome-fx .pack-compare-head { display: none; }
          .welcome-fx .pack-compare-row { grid-template-columns: 1fr; }
          .welcome-fx .pack-compare-row span:first-child { border-right: none; border-bottom: 1px dashed hsl(var(--border)); }
        }

        .welcome-fx .trust { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
        @media (max-width: 900px) { .welcome-fx .trust { grid-template-columns: 1fr 1fr; } }
        .welcome-fx .trust .panel { padding: 20px; }
        .welcome-fx .trust h4 { margin: 0 0 6px; font-size: 14px; color: hsl(var(--muted)); text-transform: uppercase; letter-spacing: 0.1em; }
        .welcome-fx .trust .num { font-size: 28px; font-weight: 700; letter-spacing: -0.02em; }
        .welcome-fx .trust p { margin: 6px 0 0; color: hsl(var(--muted)); font-size: 13px; }

        .welcome-fx .code { border: 1px solid hsl(var(--border)); background: hsl(var(--bg) / 0.6); border-radius: 12px; padding: 18px 20px; font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 13px; line-height: 1.65; overflow: auto; white-space: pre-wrap; }

        /* The loop — 4 word-tags + animated converging arcs */
        .welcome-fx .loop-section { padding: 96px 0 120px; }
        .welcome-fx .loop-stage { position: relative; margin-top: 32px; padding: 64px 8px 96px; border-radius: 18px; overflow: hidden; }
        .welcome-fx .loop-words { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; max-width: 1080px; margin: 0 auto; padding: 0 24px; position: relative; z-index: 2; }
        .welcome-fx .loop-tag { justify-self: center; padding: 10px 22px; border-radius: 999px; border: 1px solid hsl(var(--accent)); background: hsl(var(--accent) / 1); color: hsl(var(--accent-foreground)); font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 13px; letter-spacing: 0.18em; text-transform: uppercase; }
        @media (max-width: 720px) {
          .welcome-fx .loop-words { grid-template-columns: repeat(2, 1fr); gap: 12px; }
          .welcome-fx .loop-tag { font-size: 11px; padding: 8px 14px; letter-spacing: 0.14em; }
        }
        .welcome-fx .loop-art { display: block; width: 100%; height: auto; margin-top: 12px; }
        /* Base arcs — faint solid stroke, always visible */
        .welcome-fx .loop-base path { fill: none; stroke: hsl(var(--accent) / 0.35); stroke-width: 1.2; stroke-linecap: round; }
        /* Bright trace overlay — narrow lit segment racing along each arc.
           pathLength=100 normalizes the dash math regardless of geometry. */
        .welcome-fx .loop-trace path { fill: none; stroke: hsl(var(--accent)); stroke-width: 1.8; stroke-linecap: round; filter: drop-shadow(0 0 6px hsl(var(--accent) / 0.85)); stroke-dasharray: 14 100; stroke-dashoffset: 100; animation: loop-draw 4.2s linear infinite; }
        .welcome-fx .loop-path-1 { animation-delay: 0s; }
        .welcome-fx .loop-path-2 { animation-delay: 1.05s; }
        .welcome-fx .loop-path-3 { animation-delay: 2.1s; }
        .welcome-fx .loop-path-4 { animation-delay: 3.15s; }
        .welcome-fx .loop-axis { stroke: hsl(var(--accent) / 0.5); stroke-width: 1.5; }
        .welcome-fx .loop-node { fill: hsl(var(--accent)); filter: drop-shadow(0 0 12px hsl(var(--accent) / 0.85)); animation: loop-pulse 2.4s ease-in-out infinite; }
        .welcome-fx .loop-hub-ring { fill: hsl(var(--bg) / 0.6); stroke: hsl(var(--accent) / 0.55); stroke-width: 1.4; }
        /* Stage nodes inside the loop SVG — guarantees the arcs start exactly
           under each label regardless of viewport width. */
        .welcome-fx .loop-stagenode rect { fill: hsl(var(--panel)); stroke: hsl(var(--accent) / 0.4); stroke-width: 1.2; transition: stroke 0.2s ease, fill 0.2s ease, filter 0.2s ease; }
        .welcome-fx .loop-stagenode text { fill: hsl(var(--text)); font-size: 18px; font-weight: 600; letter-spacing: 0.04em; font-family: inherit; }
        .welcome-fx .loop-stage-ic path { fill: none; stroke: hsl(var(--accent)); stroke-width: 1.9; stroke-linecap: round; stroke-linejoin: round; }
        .welcome-fx .loop-stagenode:hover rect { stroke: hsl(var(--accent)); fill: hsl(var(--accent) / 0.12); filter: drop-shadow(0 0 12px hsl(var(--accent) / 0.4)); }
        .welcome-fx .loop-deliverable rect { fill: hsl(var(--accent) / 0.10); stroke: hsl(var(--accent) / 0.6); stroke-width: 1.5; filter: drop-shadow(0 14px 34px hsl(var(--accent) / 0.18)); }
        .welcome-fx .loop-deliv-title { fill: hsl(var(--text)); font-size: 22px; font-weight: 640; letter-spacing: -0.01em; font-family: inherit; }
        .welcome-fx .loop-deliv-sub { fill: hsl(var(--muted)); font-size: 13px; letter-spacing: 0.06em; font-family: 'IBM Plex Mono', ui-monospace, monospace; }
        .welcome-fx .loop-kernel { position: absolute; left: 50%; bottom: 22px; transform: translateX(-50%); display: inline-flex; align-items: center; gap: 8px; padding: 6px 14px; border-radius: 999px; border: 1px solid hsl(var(--accent) / 0.4); background: hsl(var(--bg) / 0.7); color: hsl(var(--text)); font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 11px; letter-spacing: 0.16em; text-transform: uppercase; backdrop-filter: blur(8px); }
        .welcome-fx .loop-kernel-dot { width: 6px; height: 6px; border-radius: 50%; background: hsl(var(--accent)); box-shadow: 0 0 10px hsl(var(--accent)); }
        @keyframes core-pulse {
          0%, 100% { transform: scale(1); opacity: 0.95; }
          50% { transform: scale(1.05); opacity: 1; }
        }
        @keyframes loop-draw {
          0%   { stroke-dashoffset: 100; }
          100% { stroke-dashoffset: -14; }
        }
        @keyframes loop-pulse {
          0%, 100% { r: 5; opacity: 0.85; }
          50%      { r: 8; opacity: 1; }
        }
        @keyframes arch-flow {
          0%   { stroke-dashoffset: 72; }
          100% { stroke-dashoffset: -16; }
        }
        @media (prefers-reduced-motion: reduce) {
          .welcome-fx .loop-trace path { animation: none; stroke-dasharray: none; stroke-dashoffset: 0; opacity: 0.6; }
          .welcome-fx .loop-node { animation: none; }
          .welcome-fx .hero-trace path { animation: none; stroke-dasharray: none; stroke-dashoffset: 0; opacity: 0.55; }
          .welcome-fx .arch-link-trace path { animation: none; stroke-dasharray: none; stroke-dashoffset: 0; opacity: 0.55; }
        }

        .welcome-fx .cta-final {
          padding: 96px 0; text-align: center; color: hsl(var(--text));
          border-top-color: hsl(var(--border)); background-color: hsl(var(--bg));
          background-image:
            linear-gradient(90deg, transparent, hsl(var(--accent) / .08), transparent),
            repeating-linear-gradient(135deg, hsl(var(--border) / .16) 0 1px, transparent 1px 12px);
        }
        .welcome-fx .cta-final h2 { margin: 0 auto 14px; color: hsl(var(--text)); }
        .welcome-fx .cta-final p { margin: 0 auto 28px; max-width: 580px; color: hsl(var(--muted)); font-size: 17px; }
        .welcome-fx .cta-final .cta-identity-line { color: hsl(var(--muted)); }

        /* Editorial enterprise system — final cascade */
        .welcome-fx {
          position: relative;
          overflow: clip;
          background: hsl(var(--bg));
          background-image:
            linear-gradient(90deg, hsl(var(--text) / .026) 1px, transparent 1px),
            linear-gradient(hsl(var(--text) / .026) 1px, transparent 1px);
          background-size: 132px 132px;
        }
        .welcome-fx .container { max-width: 1440px; padding-inline: 24px; }
        .welcome-fx section { padding: clamp(92px, 10vw, 150px) 0; border-bottom: 1px solid hsl(var(--border)); }
        .welcome-fx .btn,
        .welcome-fx .pill,
        .welcome-fx .cap-chip,
        .welcome-fx .hg-chip,
        .welcome-fx .pack-ladder-step,
        .welcome-fx .pack-kicker,
        .welcome-fx .pack-detail-tab,
        .welcome-fx .pack-targets span,
        .welcome-fx .orb-checks span,
        .welcome-fx .footer-links a { border-radius: 1px; box-shadow: none; }
        .welcome-fx .btn { text-transform: uppercase; letter-spacing: .04em; font-size: 11px; }
        .welcome-fx .btn:hover { transform: none; }

        .welcome-fx .hero {
          padding-top:12px;
          border-bottom: 1px solid hsl(var(--border));
          overflow: visible;
        }
        .welcome-fx .hero::before { content: none; }
        .welcome-fx .hero::after {
          content: "";
          position: absolute;
          top: 2px;
          right: max(24px, calc((100vw - 1392px) / 2));
          color: hsl(var(--muted));
          font-family: 'IBM Plex Mono', ui-monospace, monospace;
          font-size: 9px;
          letter-spacing: .13em;
        }
        .welcome-fx .grid-bg { opacity: 0; }
        .welcome-fx .hero-grid { 
            grid-template-columns: minmax(480px, 1fr) minmax(0, 1.1fr); 
            gap: clamp(32px, 4vw, 64px); 
            align-items: center; 
          }
            
        .welcome-fx .hero-copy { 
            max-width: 680px; 
            padding-left: 22px; 
            border-left: 1px solid hsl(var(--border)); 
          }
        .welcome-fx .hero-kicker,
        .welcome-fx .section-eyebrow,
        .welcome-fx .action-kicker,
        .welcome-fx .positioning-kicker {
          color: hsl(var(--accent));
          font-family: 'IBM Plex Mono', ui-monospace, monospace;
          font-size: 10px;
          letter-spacing: .14em;
          text-transform: uppercase;
        }
        .welcome-fx .hero-title,
        .welcome-fx .section-head h2,
        .welcome-fx .positioning-intro h2,
        .welcome-fx .cta-final h2 {
          font-family: 'IBM Plex Sans', sans-serif;
          font-weight: 400;
          letter-spacing: -.035em;
        }
        .welcome-fx .hero-title { max-width: 14ch; margin: 22px 0 28px; font-size: clamp(3.5rem, 5.3vw, 5.8rem); line-height: .86; }
        .welcome-fx .grad { color: hsl(var(--text)); }
        .welcome-fx .lede { 
          max-width: 52ch; 
          padding-left: 18px; 
          border-left: 2px solid hsl(var(--accent)); 
          color: hsl(var(--text)); 
          font-size: 17px; 
          line-height: 1.58; 
        }
        .welcome-fx .hero-cta { gap: 0; }
        .welcome-fx .hero-cta > * + * { margin-left: -1px; }
        .welcome-fx .capabilities-strip { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0; border-top: 1px solid hsl(var(--border)); border-left: 1px solid hsl(var(--border)); }
        .welcome-fx .cap-chip { padding: 10px 12px; border: 0; border-right: 1px solid hsl(var(--border)); border-bottom: 1px solid hsl(var(--border)); background: transparent; font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 9px; }
        .welcome-fx .persona-paths { gap: 0; }
        .welcome-fx .persona-paths a { padding: 8px 14px; border-right: 1px solid hsl(var(--border)); font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 10px; text-transform: uppercase; }

        .welcome-fx .hero-video-slot { min-width: 0; }
        .welcome-fx .hero-video-slot > .hg-toolbar,
        .welcome-fx .hero-video-slot > .hg-canvas,
        .welcome-fx .hero-video-slot > .hg-stats { display: none; }
        .welcome-fx .hero-diagram { padding: 28px; border: 1px solid hsl(var(--border)); background: hsl(var(--panel) / .45); }
        .welcome-fx .hg-toolbar { gap: 0; margin: -28px -28px 18px; border-bottom: 1px solid hsl(var(--border)); }
        .welcome-fx .hg-chip { flex: 1; padding: 13px 14px; border: 0; border-right: 1px solid hsl(var(--border)); background: transparent; text-align: center; letter-spacing: .1em; text-transform: uppercase; }
        .welcome-fx .hg-chip.active { color: hsl(var(--bg)); background: hsl(var(--text)); border-color: hsl(var(--border)); }
        .welcome-fx .hg-canvas { display: block; }
        .welcome-fx .hg-dimensional-scene {
          position: relative; min-height: 500px; margin: 0 -8px;
          perspective: 1100px; perspective-origin: 50% 28%; overflow: hidden;
          background: linear-gradient(90deg, transparent 49.9%, hsl(var(--border) / .55) 50%, transparent 50.1%);
        }
        .welcome-fx .hg-dimensional-scene::before {
          content: "LIVE ARCHITECTURE / RUNTIME STRATA"; position: absolute; left: 18px; top: 18px;
          font: 9px/1 'IBM Plex Mono', ui-monospace, monospace; letter-spacing: .14em; color: hsl(var(--muted));
        }
        .welcome-fx .hg-scene-layer {
          position: absolute; left: 10%; width: 80%; height: 118px; padding: 25px 30px;
          display: grid; grid-template-columns: 54px 1fr auto; grid-template-rows: auto auto; align-items: center;
          border: 1px solid hsl(var(--border)); color: hsl(var(--text)); background: hsl(var(--bg) / .96);
          box-shadow: 0 18px 0 -12px hsl(var(--text) / .05), 0 26px 42px hsl(var(--text) / .08);
          transform: rotateX(57deg) rotateZ(-4deg); transform-origin: 50% 50%;
          transition: transform .32s ease, border-color .32s ease, background .32s ease;
          outline: none;
        }
        .welcome-fx .hg-scene-layer:hover,
        .welcome-fx .hg-scene-layer:focus-visible { transform: rotateX(50deg) rotateZ(-2deg) translateY(-14px); border-color: hsl(var(--accent)); }
        .welcome-fx .hg-scene-control { top: 70px; z-index: 3; }
        .welcome-fx .hg-scene-runtime { top: 187px; z-index: 2; left: 13%; width: 74%; background: hsl(var(--accent) / .10); }
        .welcome-fx .hg-scene-context { top: 304px; z-index: 1; left: 16%; width: 68%; }
        .welcome-fx .hg-scene-index { grid-row: 1 / 3; font: 10px/1 'IBM Plex Mono', ui-monospace, monospace; color: hsl(var(--accent)); }
        .welcome-fx .hg-scene-layer strong { font-size: 20px; font-weight: 600; letter-spacing: -.02em; }
        .welcome-fx .hg-scene-layer small { grid-column: 2; color: hsl(var(--muted)); font: 10px/1.4 'IBM Plex Mono', ui-monospace, monospace; text-transform: uppercase; letter-spacing: .07em; }
        .welcome-fx .hg-scene-layer i { grid-column: 3; grid-row: 1 / 3; font: normal 11px/1 'IBM Plex Mono', ui-monospace, monospace; color: hsl(var(--accent)); }
        .welcome-fx .hg-scene-axis { position: absolute; inset: 75px 50% 64px auto; width: 1px; background: hsl(var(--accent) / .32); z-index: 5; pointer-events: none; }
        .welcome-fx .hg-scene-axis span { position: absolute; left: -4px; width: 9px; height: 9px; border: 1px solid hsl(var(--accent)); background: hsl(var(--bg)); transform: rotate(45deg); }
        .welcome-fx .hg-scene-axis span:nth-child(1) { top: 16%; } .welcome-fx .hg-scene-axis span:nth-child(2) { top: 50%; } .welcome-fx .hg-scene-axis span:nth-child(3) { top: 84%; }
        .welcome-fx .hg-scene-signal { position: absolute; right: 18px; bottom: 18px; font: 9px/1 'IBM Plex Mono', ui-monospace, monospace; text-transform: uppercase; letter-spacing: .12em; color: hsl(var(--muted)); }
        .welcome-fx .hg-ring { stroke: hsl(var(--accent) / .42); stroke-width: 1.2; }
        .welcome-fx .hg-core { filter: drop-shadow(0 12px 24px hsl(205 82% 18% / .45)); }
        .welcome-fx .hg-node-stack path { fill: hsl(var(--panel) / .45); stroke: hsl(var(--accent) / .55); stroke-width: 1; }
        .welcome-fx .hg-node-stack path:nth-child(2) { stroke: hsl(var(--border)); }
        .welcome-fx .hg-node-stack path:nth-child(3) { fill: hsl(var(--panel)); }
        .welcome-fx .hg-node-stack circle { fill: hsl(var(--accent)); filter: drop-shadow(0 0 5px hsl(var(--accent))); }
        .welcome-fx .hg-flow-path {
          stroke-width: 1.5;
          stroke-linecap: round;
          stroke-linejoin: round;
          opacity: .9;
          vector-effect: non-scaling-stroke;
        }
        .welcome-fx .hg-edge-bg {
          fill: hsl(var(--bg) / .94);
          stroke: hsl(var(--border));
          stroke-width: .7;
          rx: 5px;
        }
        .welcome-fx .hg-edge-label { fill: hsl(var(--text) / .76); paint-order: stroke; stroke: hsl(var(--bg) / .8); stroke-width: .8px; }
        .welcome-fx .hg-node-name { letter-spacing: -.01em; }
        .welcome-fx .hg-node-sub { letter-spacing: .01em; }
        .welcome-fx .hg-node { transition: none; }
        .welcome-fx .hg-node:hover { transform: none; }
        .welcome-fx .hg-node-box { rx: 1px; filter: none; }
        .welcome-fx .hg-node-iconbox { rx: 1px; }
        .welcome-fx .hg-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0; margin: 12px -28px -28px; padding: 0; border-top: 1px solid hsl(var(--border)); }
        .welcome-fx .hg-stats span { padding: 14px; border-right: 1px solid hsl(var(--border)); text-align: center; }

        .welcome-fx .section-head {
          position: relative;
          display: grid;
          grid-template-columns: minmax(0, 1.25fr) minmax(260px, .75fr);
          gap: 42px;
          align-items: end;
          max-width: none;
          margin-bottom: 52px;
          padding-top: 20px;
          border-top: 1px solid hsl(var(--border));
          text-align: left;
        }
        .welcome-fx .section-head::after {
          content: "SYSTEM NOTE / ENTERPRISE ARCHITECTURE";
          position: absolute;
          top: -24px;
          right: 0;
          color: hsl(var(--muted));
          font-family: 'IBM Plex Mono', ui-monospace, monospace;
          font-size: 9px;
          letter-spacing: .12em;
        }
        .welcome-fx .section-head h2 { max-width: 16ch; margin: 10px 0 0; font-size: clamp(3.2rem, 5vw, 5.8rem); line-height: .93; }
        .welcome-fx .section-sub { max-width: 52ch; margin: 0; color: hsl(var(--muted)); line-height: 1.7; }

        .welcome-fx .portfolio-fx,
        .welcome-fx .roi-fx,
        .welcome-fx .exec-fx,
        .welcome-fx .dt-fx,
        .welcome-fx .solution-fx,
        .welcome-fx .semantic-fx,
        .welcome-fx .dev-fx { border-bottom: 1px solid hsl(var(--border)); background: transparent; }
        .welcome-fx .portfolio-fx .container,
        .welcome-fx .roi-fx .container,
        .welcome-fx .exec-fx .container,
        .welcome-fx .dev-fx .container { max-width: 1440px; padding-inline: 24px; }
        .welcome-fx .portfolio-head h2,
        .welcome-fx .roi-fx h2,
        .welcome-fx .exec-fx h2,
        .welcome-fx .dev-fx h2 {
          font-family: 'IBM Plex Sans', sans-serif;
          font-weight: 400;
          letter-spacing: -.035em;
          line-height: .94;
        }
        .welcome-fx .portfolio-card,
        .welcome-fx .portfolio-next,
        .welcome-fx .roi-tierstack-wrap,
        .welcome-fx .roi-timeline,
        .welcome-fx .roi-stat,
        .welcome-fx .roi-ledger,
        .welcome-fx .exec-card,
        .welcome-fx .exec-example,
        .welcome-fx .exec-col,
        .welcome-fx .dt-fx-card,
        .welcome-fx .dev-keyrail-chip,
        .welcome-fx .dev-keyrail-run,
        .welcome-fx .dev-win {
          border-radius: 1px;
          box-shadow: none;
        }
        .welcome-fx .roi-stats,
        .welcome-fx .roi-ledgers,
        .welcome-fx .exec-grid,
        .welcome-fx .exec-roi { gap: 0; }
        .welcome-fx .portfolio-grid { gap: 12px; }
        .welcome-fx .portfolio-card:hover,
        .welcome-fx .roi-btn:hover,
        .welcome-fx .dev-keyrail-chip:hover { transform: none; box-shadow: inset 3px 0 0 hsl(var(--accent)); }
        .welcome-fx .portfolio-filters button,
        .welcome-fx .portfolio-search,
        .welcome-fx .portfolio-status,
        .welcome-fx .portfolio-tag,
        .welcome-fx .roi-btn,
        .welcome-fx .exec-col-tag,
        .welcome-fx .dt-fx-foot-tag,
        .welcome-fx .dev-tab { border-radius: 1px; }

        .welcome-fx .fabric-fx .fabric-layer,
        .welcome-fx .fabric-fx .fabric-flow,
        .welcome-fx .fabric-fx .fabric-node,
        .welcome-fx .fabric-fx .fabric-node-tags span,
        .welcome-fx .fabric-fx .fabric-card,
        .welcome-fx .fabric-fx .fabric-card code { border-radius: 0; box-shadow: none; }
        .welcome-fx .fabric-fx .fabric-layers {
          display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0; min-height: 0; margin: 10px 0 36px;
        }
        .welcome-fx .fabric-fx .fabric-layer {
          position: relative; inset: auto; width: auto; min-height: 150px; padding: 24px 26px;
          background: hsl(var(--bg) / .92); transform: none; outline: none;
          transition: background .2s ease, box-shadow .2s ease;
        }
        .welcome-fx .fabric-fx .fabric-layer + .fabric-layer { margin-left: -1px; }
        .welcome-fx .fabric-fx .fabric-layer:hover,
        .welcome-fx .fabric-fx .fabric-layer:focus-visible { transform: none; background: hsl(var(--accent) / .07); box-shadow: inset 0 -3px 0 hsl(var(--accent)); }
        .welcome-fx .fabric-fx .fabric-layer-accent { color: hsl(var(--accent-foreground)); background: hsl(var(--accent)); }
        .welcome-fx .fabric-fx .fabric-cards { gap: 0; }
        .welcome-fx .fabric-fx .fabric-card + .fabric-card { margin-top: -1px; }
        .welcome-fx .fabric-fx .fabric-card { background: transparent; }

        .welcome-fx .ledger,
        .welcome-fx .why-card,
        .welcome-fx .panel,
        .welcome-fx .feat,
        .welcome-fx .obj-grid,
        .welcome-fx .arch,
        .welcome-fx .node,
        .welcome-fx .pack-compare,
        .welcome-fx .pack-flow,
        .welcome-fx .code,
        .welcome-fx .hero-art {
          border-radius: 1px;
          background: transparent;
          box-shadow: none;
        }
        .welcome-fx .feat::after,
        .welcome-fx .arch::before { content: none; }
        .welcome-fx .panel:hover,
        .welcome-fx .feat:hover,
        .welcome-fx .node:hover { transform: none; box-shadow: inset 3px 0 0 hsl(var(--accent)); }
        .welcome-fx .feat .ic,
        .welcome-fx .arch-stage,
        .welcome-fx .ledger-tag,
        .welcome-fx .why-card-kicker { border-radius: 1px; box-shadow: none; }

        .welcome-fx .positioning-band { background: transparent; }
        .welcome-fx .positioning-band::before,
        .welcome-fx .positioning-band::after { content: none; }
        .welcome-fx .positioning-intro { padding-block: 86px; }
        .welcome-fx .positioning-intro h2 { line-height: .94; }
        .welcome-fx .positioning-principles article { padding: 72px 34px; background: transparent; }
        .welcome-fx .positioning-principles article::after { content: none; }
        .welcome-fx .positioning-principles article:hover { background: hsl(var(--text) / .025); box-shadow: none; }
        .welcome-fx .positioning-ghost { display: none; }

        .welcome-fx .act-grid,
        .welcome-fx .trust { gap: 0; border-top: 1px solid hsl(var(--border)); border-left: 1px solid hsl(var(--border)); }
        .welcome-fx .act-step,
        .welcome-fx .trust .panel { border: 0; border-right: 1px solid hsl(var(--border)); border-bottom: 1px solid hsl(var(--border)); border-radius: 0; background: transparent; box-shadow: none; }
        .welcome-fx .act-step:hover { transform: none; box-shadow: inset 3px 0 0 hsl(var(--accent)); }
        .welcome-fx .act-step-no { border-radius: 0; font-family: 'IBM Plex Mono', ui-monospace, monospace; }
        .welcome-fx .mid-cta { background: transparent; }

        .welcome-fx .pack-ladder { justify-content: flex-start; gap: 0; }
        .welcome-fx .pack-ladder-step { border-right: 0; background: transparent; }
        .welcome-fx .pack-tabs { justify-content: flex-start !important; gap: 0 !important; }
        .welcome-fx .pack-tabs button { border-radius: 0 !important; }
        .welcome-fx .pack-card { gap: 0; padding: 0; border-radius: 0; background: transparent; }
        .welcome-fx .pack-main,
        .welcome-fx .pack-side-card,
        .welcome-fx .orb-panel,
        .welcome-fx .orb-hero,
        .welcome-fx .pack-why,
        .welcome-fx .pack-deploy,
        .welcome-fx .pack-consulting,
        .welcome-fx .pack-library { border-radius: 0; background: transparent; box-shadow: none; }
        .welcome-fx .pack-main { padding: 30px; border-right: 0; }
        .welcome-fx .pack-metrics,
        .welcome-fx .pack-include-grid { gap: 0; border-top: 1px solid hsl(var(--border)); border-left: 1px solid hsl(var(--border)); }
        .welcome-fx .pack-metrics > div,
        .welcome-fx .pack-include-grid span { border: 0; border-right: 1px solid hsl(var(--border)); border-bottom: 1px solid hsl(var(--border)); border-radius: 0; background: transparent; }
        .welcome-fx .pack-detail-tabs { gap: 0; margin: 0; }
        .welcome-fx .pack-detail-tab { min-height: 54px; background: transparent; transform: none; }
        .welcome-fx .pack-detail-tab:hover { transform: none; box-shadow: none; }
        .welcome-fx .pack-detail-tab.on { color: hsl(var(--bg)); background: hsl(var(--text)); border-color: hsl(var(--text)); box-shadow: none; }
        .welcome-fx .uc { border-radius: 0; background: transparent; box-shadow: none; }
        .welcome-fx .uc:hover { transform: none; box-shadow: inset 3px 0 0 hsl(var(--accent)); }
        .welcome-fx .orb-panel { padding: 30px; }
        .welcome-fx .orb-checks { gap: 0; }
        .welcome-fx .orb-checks span { color: hsl(var(--text)); background: transparent; border-color: hsl(var(--border)); }

        .welcome-fx .loop-section { padding-block: clamp(92px, 10vw, 150px); }
        .welcome-fx .loop-stage { margin-top: 0; padding: 44px 0 88px; border-radius: 0; border-top: 1px solid hsl(var(--border)); border-bottom: 1px solid hsl(var(--border)); }
        .welcome-fx .loop-art { display: block; }
        .welcome-fx .loop-layer-stack { position: relative; min-height: 630px; perspective: 1200px; perspective-origin: 50% 10%; }
        .welcome-fx .loop-layer {
          position: absolute; left: 10%; width: 80%; min-height: 108px; padding: 24px 34px;
          display: grid; grid-template-columns: 58px 180px 1fr; align-items: center;
          border: 1px solid hsl(var(--border)); background: hsl(var(--bg) / .96);
          box-shadow: 0 26px 45px hsl(var(--text) / .07); transform: rotateX(55deg) rotateZ(-2.5deg);
          transition: transform .3s ease, border-color .3s ease; outline: none;
        }
        .welcome-fx .loop-layer:hover,
        .welcome-fx .loop-layer:focus-visible { transform: rotateX(48deg) rotateZ(-1deg) translateY(-13px); border-color: hsl(var(--accent)); }
        .welcome-fx .loop-layer:nth-child(1) { top: 24px; z-index: 5; }
        .welcome-fx .loop-layer:nth-child(2) { top: 139px; left: 12%; width: 76%; z-index: 4; }
        .welcome-fx .loop-layer:nth-child(3) { top: 254px; left: 14%; width: 72%; z-index: 3; }
        .welcome-fx .loop-layer:nth-child(4) { top: 369px; left: 16%; width: 68%; z-index: 2; background: hsl(var(--accent) / .10); }
        .welcome-fx .loop-layer > span { font: 10px/1 'IBM Plex Mono', ui-monospace, monospace; color: hsl(var(--accent)); }
        .welcome-fx .loop-layer > strong { font-size: 22px; font-weight: 600; }
        .welcome-fx .loop-layer > small { color: hsl(var(--muted)); font: 11px/1.5 'IBM Plex Mono', ui-monospace, monospace; }
        .welcome-fx .loop-layer-output { position: absolute; z-index: 1; left: 22%; bottom: 12px; width: 56%; padding: 22px; border: 1px solid hsl(var(--accent)); background: hsl(var(--text)); color: hsl(var(--bg)); text-align: center; }
        .welcome-fx .loop-layer-output span,
        .welcome-fx .loop-layer-output small { display: block; font: 9px/1.5 'IBM Plex Mono', ui-monospace, monospace; letter-spacing: .1em; text-transform: uppercase; }
        .welcome-fx .loop-layer-output strong { display: block; margin: 5px 0; font-size: 20px; }
        .welcome-fx .loop-stage-stack polygon { fill: hsl(var(--panel) / .45); stroke: hsl(var(--accent) / .52); stroke-width: 1; }
        .welcome-fx .loop-stage-stack polygon:nth-child(2) { stroke: hsl(var(--border)); }
        .welcome-fx .loop-stage-stack polygon:nth-child(3) { fill: hsl(var(--panel)); }
        .welcome-fx .loop-stage-stack circle { fill: hsl(var(--accent)); filter: drop-shadow(0 0 6px hsl(var(--accent))); }
        .welcome-fx .loop-stagenode .loop-stage-meta { fill: hsl(var(--muted)); font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 8px; font-weight: 400; letter-spacing: .08em; text-transform: uppercase; }
        .welcome-fx .loop-stagenode rect,
        .welcome-fx .loop-deliverable rect { rx: 1px; filter: none; }
        .welcome-fx .loop-base path { stroke-width: 1.35; stroke-linecap: round; stroke-linejoin: round; opacity: .72; vector-effect: non-scaling-stroke; }
        .welcome-fx .loop-trace path { stroke-width: 1.65; stroke-linecap: round; stroke-linejoin: round; opacity: .92; vector-effect: non-scaling-stroke; filter: drop-shadow(0 0 5px hsl(var(--accent) / .72)); }
        .welcome-fx .loop-node { stroke: hsl(var(--bg)); stroke-width: 2.5; filter: drop-shadow(0 0 8px hsl(var(--accent) / .9)); }
        .welcome-fx .loop-hub-ring { fill: hsl(var(--bg) / .92); stroke-width: 1.25; }
        .welcome-fx .loop-axis { stroke-linecap: round; opacity: .78; }
        .welcome-fx .loop-stage-meta { paint-order: stroke; stroke: hsl(var(--bg) / .94); stroke-width: 3px; stroke-linejoin: round; }
        .welcome-fx .loop-deliverable rect { fill: hsl(var(--panel)); stroke-width: 1.25; }
        .welcome-fx .loop-kernel { border-radius: 1px; backdrop-filter: none; }

        .welcome-fx .cta-final { padding-block: 110px; color: hsl(var(--bg)); background: hsl(var(--text)); background-image: none; }
        .welcome-fx .cta-final h2 { max-width: 14ch; color: hsl(var(--bg)); font-size: clamp(3.2rem, 5vw, 5.7rem); line-height: .94; }
        .welcome-fx .cta-final p,
        .welcome-fx .cta-final .cta-identity-line { color: hsl(var(--bg) / .68); }
        .welcome-fx .cta-final .btn-primary { color: hsl(var(--text)); background: hsl(var(--bg)); }
        .welcome-fx .cta-final .btn { color: hsl(var(--bg)); border-color: hsl(var(--bg) / .32); }
        .welcome-fx .cta-final .btn-primary { color: hsl(var(--text)); }

        @media (max-width: 1100px) {
          .welcome-fx .hero { min-height: auto; }
          .welcome-fx .hero-grid { grid-template-columns: 1fr; }
          .welcome-fx .hero-copy { max-width: 760px; }
          .welcome-fx .hero-diagram { max-width: 820px; margin: 0; }
          .welcome-fx .section-head { grid-template-columns: 1fr; gap: 22px; }
          .welcome-fx .pack-card { grid-template-columns: 1fr; }
          .welcome-fx .pack-main { border-right: 1px solid hsl(var(--border)); }
        }
        @media (max-width: 720px) {
          .welcome-fx .fabric-fx .fabric-layers { display: grid; min-height: 0; perspective: none; }
          .welcome-fx .fabric-fx .fabric-layer,
          .welcome-fx .fabric-fx .fabric-layer:nth-child(1),
          .welcome-fx .fabric-fx .fabric-layer:nth-child(2),
          .welcome-fx .fabric-fx .fabric-layer:nth-child(3) { position: relative; inset: auto; width: 100%; min-height: 0; transform: none; box-shadow: none; }
          .welcome-fx .fabric-fx .fabric-layer:hover,
          .welcome-fx .fabric-fx .fabric-layer:focus-visible { transform: translateX(5px); }
          .welcome-fx .fabric-fx .fabric-layer + .fabric-layer { margin-top: -1px; margin-left: 0; }
          .welcome-fx .container { padding-inline: 18px; }
          .welcome-fx section { padding-block: 78px; }
          .welcome-fx .hero { padding: 72px 0 56px; }
          .welcome-fx .hero::after { left: 18px; right: auto; top: 24px; }
          .welcome-fx .hero-copy { min-width: 0; padding-left: 16px; }
          .welcome-fx .hero-title { max-width: 100%; font-size: clamp(3.5rem, 16vw, 5rem); }
          .welcome-fx .hero-cta { display: grid; }
          .welcome-fx .hero-cta > * + * { margin: -1px 0 0; }
          .welcome-fx .capabilities-strip { grid-template-columns: 1fr 1fr; }
          .welcome-fx .persona-paths { display: grid; }
          .welcome-fx .persona-paths a { border-right: 0; border-bottom: 1px solid hsl(var(--border)); }
          .welcome-fx .hero-diagram { padding: 18px; }
          .welcome-fx .hg-toolbar { margin: -18px -18px 12px; }
          .welcome-fx .hg-stats { margin: 10px -18px -18px; grid-template-columns: 1fr; }
          .welcome-fx .hg-stats span { border-right: 0; border-bottom: 1px solid hsl(var(--border)); }
          .welcome-fx .hg-dimensional-scene { min-height: 415px; }
          .welcome-fx .hg-scene-layer { left: 4%; width: 92%; height: 102px; padding: 20px 16px; grid-template-columns: 38px 1fr auto; }
          .welcome-fx .hg-scene-runtime { top: 155px; left: 7%; width: 86%; }
          .welcome-fx .hg-scene-context { top: 255px; left: 10%; width: 80%; }
          .welcome-fx .hg-scene-layer strong { font-size: 16px; }
          .welcome-fx .hg-scene-layer small { font-size: 8px; }
          .welcome-fx .section-head { padding-right: 0; margin-bottom: 38px; }
          .welcome-fx .section-head::after { display: none; }
          .welcome-fx .section-head h2 { max-width: none; font-size: clamp(2.8rem, 13vw, 4.2rem); }
          .welcome-fx .positioning-intro { padding-block: 52px; }
          .welcome-fx .positioning-intro h2 { font-size: 3rem; }
          .welcome-fx .positioning-principles article { padding: 38px 0; }
          .welcome-fx .pack-tabs { flex-wrap: nowrap !important; overflow-x: auto; justify-content: flex-start !important; }
          .welcome-fx .pack-tabs button { flex: 0 0 auto; }
          .welcome-fx .pack-main,
          .welcome-fx .orb-panel { padding: 24px 20px; }
          .welcome-fx .pack-metrics { grid-template-columns: 1fr; }
          .welcome-fx .loop-layer-stack { min-height: 570px; }
          .welcome-fx .loop-layer { left: 0; width: 100%; padding: 20px 18px; grid-template-columns: 38px 90px 1fr; }
          .welcome-fx .loop-layer:nth-child(2) { left: 3%; width: 94%; }
          .welcome-fx .loop-layer:nth-child(3) { left: 6%; width: 88%; }
          .welcome-fx .loop-layer:nth-child(4) { left: 9%; width: 82%; }
          .welcome-fx .loop-layer > strong { font-size: 17px; }
          .welcome-fx .loop-layer > small { font-size: 9px; }
          .welcome-fx .loop-layer-output { left: 8%; width: 84%; }
          .welcome-fx .loop-stagenode > text:not(.loop-stage-meta) { font-size: 26px; }
          .welcome-fx .loop-stagenode .loop-stage-meta { display: none; }
          .welcome-fx .cta-final { padding-block: 82px; text-align: left; }
          .welcome-fx .cta-final h2 { margin-left: 0; }
        }
      `}</style>
    </div>
  );
}
